import { API_ENDPOINTS, API_CONFIG } from './apiConfig';
import { getCandidateId } from './authService';

/**
 * Email-based sign in for interview access using channelplayJobs endpoint
 * @param {string} email - Email address
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const signInWithEmail = async (email) => {
  try {
    const response = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.loginMicroservicePath}channelplayJobs/generateOTP?mobileNumber=&emailId=${encodeURIComponent(email)}&isClient=true`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({})
    });

    // Handle both JSON and plain text responses
    let data;
    const responseText = await response.text();
    console.log('[API] Generate OTP Response:', { status: response.status, responseText });
    
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      // Response is plain text, not JSON
      data = { message: responseText };
    }
    
    // Check for success message in response data
    if (response.ok || (data && (data.message === 'OTP sent' || data.status === 'success' || responseText === 'OTP sent'))) {
      return { success: true, data };
    } else {
      return { success: false, error: data.message || 'Failed to send OTP' };
    }
  } catch (error) {
    console.error('Email sign-in error:', error);
    return { success: false, error: 'Network error. Please try again.' };
  }
};

/**
 * Update WhatsApp engagement flag for Jobs app
 * @param {string|number} candidateId - Candidate ID from URL parameter
 * @returns {Promise<{success: boolean, status?: number, message?: string}>}
 */
export const updateWhatsAppEngagementForJobsApp = async (candidateId) => {
  try {
    if (candidateId === undefined || candidateId === null || candidateId === '') {
      console.warn('[ApiService] updateWhatsAppEngagementForJobsApp skipped: candidateId missing');
      return { success: false, message: 'candidateId is required' };
    }

    const url = new URL(API_ENDPOINTS.updateWhatsAppEngagementForJobsApp);
    url.searchParams.append('candidateId', String(candidateId));

    console.log('[ApiService] Updating WhatsApp engagement flag for candidate:', candidateId);

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Accept': 'text/plain, */*'
      }
    });

    const responseText = await response.text();
    const success = response.ok;

    if (!success) {
      console.warn('[ApiService] WhatsApp engagement update failed', response.status, responseText);
    }

    return {
      success,
      status: response.status,
      message: responseText || undefined
    };
  } catch (error) {
    console.error('[ApiService] Error updating WhatsApp engagement flag:', error);
    return {
      success: false,
      message: error.message || 'Network error'
    };
  }
};

/**
 * Fetch location suggestions for an input string using channelplayJobs/getLocationResults
 * @param {string} input
 * @returns {Promise<{success:boolean, data?:Array<string>, error?:string, status?:number}>}
 */
export const getLocationResults = async (input) => {
  try {
    const q = (input || '').trim();
    if (!q || q.length < 3) {
      return { success: true, data: [] };
    }
    const url = new URL(API_ENDPOINTS.getLocationResults);
    url.searchParams.append('input', q);
    const resp = await apiRequest(url.toString(), { method: 'GET' });
    if (!resp.success) return resp;
    const raw = resp.data;
    let suggestions = [];
    if (Array.isArray(raw)) {
      // Prefer objects with label/placeId when present
      suggestions = raw.map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const label = item.description || item.name || item.city || item.formattedAddress || item.placeName || '';
          const placeId = item.place_id || item.placeId || '';
          return placeId && label ? { label, placeId } : label;
        }
        return '';
      }).filter(Boolean);
    } else if (raw && typeof raw === 'object') {
      const arr = raw.predictions || raw.results || raw.items || [];
      if (Array.isArray(arr)) {
        suggestions = arr.map((it) => {
          if (!it || typeof it !== 'object') return '';
          const label = it.description || it.name || it.city || it.formattedAddress || it.placeName || '';
          const placeId = it.place_id || it.placeId || '';
          return placeId && label ? { label, placeId } : label;
        }).filter(Boolean);
      }
    }
    return { success: true, data: suggestions, status: resp.status };
  } catch (error) {
    return { success: false, error: error.message || 'Network error', status: 500 };
  }
};

/**
 * Fetch place details for a given placeId and extract latitude/longitude and pincode.
 * Backend endpoint: channelplayJobs/fetchPlaceIdDetails?placeId=
 * @param {string} placeId
 * @returns {Promise<{success:boolean, data?:{latitude:number, longitude:number, pincode?:string}, error?:string, status?:number}>}
 */
export const fetchPlaceIdDetails = async (placeId) => {
  try {
    const pid = String(placeId || '').trim();
    if (!pid) return { success: false, error: 'placeId is required', status: 400 };
    const url = `${API_CONFIG.baseUrl}${API_CONFIG.loginMicroservicePath}channelplayJobs/fetchPlaceIdDetails?placeId=${encodeURIComponent(pid)}`;
    const resp = await apiRequest(url, { method: 'GET' });
    if (!resp.success) return resp;
    const raw = resp.data;
    let lat, lng, pincode;
    if (raw) {
      const loc = raw?.result?.geometry?.location || raw?.geometry?.location || raw?.location;
      if (loc) {
        lat = Number(loc.lat);
        lng = Number(loc.lng);
      }
      if ((!Number.isFinite(lat) || !Number.isFinite(lng)) && (raw.lat !== undefined && raw.lng !== undefined)) {
        lat = Number(raw.lat);
        lng = Number(raw.lng);
      }
      // Extract pincode if available in the response
      pincode = raw.pincode || raw?.result?.pincode || '';
    }
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      const result = { latitude: lat, longitude: lng };
      if (pincode) {
        result.pincode = pincode;
      }
      return { success: true, data: result, status: resp.status };
    }
    return { success: false, error: 'No coordinates in place details', status: resp.status };
  } catch (error) {
    return { success: false, error: error.message || 'Network error', status: 500 };
  }
};

/**
 * Save candidate location if not applied
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {string} pincode - Pincode (optional, can be empty string)
 * @returns {Promise<{success:boolean, data?:any, error?:string, status?:number}>}
 */
export const saveCandidateLocation = async (lat, lng, pincode = '') => {
  try {
    const mobileNumber = getMobileNumber();
    if (!mobileNumber || mobileNumber === '0') {
      console.log('[ApiService] Cannot save location: empty mobile number');
      return { success: false, error: 'Mobile number not found', status: 400 };
    }
    
    // Construct URL with query parameters
    const url = new URL(API_ENDPOINTS.saveCandidateLocation);
    url.searchParams.append('mobileNumber', mobileNumber);
    url.searchParams.append('lat', lat.toString());
    url.searchParams.append('lng', lng.toString());
    url.searchParams.append('pincode', pincode);
    
    console.log('[ApiService] Saving candidate location:', url.toString());
    
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/plain, */*'
      }
    });
    
    const responseText = await response.text();
    const success = response.ok;
    
    if (success) {
      console.log('[ApiService] Saved candidate location successfully');
      return { success: true, status: response.status, data: responseText };
    } else {
      console.log('[ApiService] Failed to save candidate location:', response.status, responseText);
      return { success: false, status: response.status, error: responseText || 'Failed to save location' };
    }
  } catch (error) {
    console.error('[ApiService] Error saving candidate location:', error);
    return { success: false, error: error.message || 'Network error', status: 500 };
  }
};

/**
 * Fetch last applied jobs for a candidate
 * @param {string|number} [candidateIdParam] - Optional candidateId; if not provided, read from storage
 * @param {number} [size=5] - Number of items to fetch (default 5)
 * @returns {Promise<{success:boolean, data?:any, error?:string, status?:number}>}
 */
export const getLastAppliedJobs = async (candidateIdParam = null, size = 5) => {
  try {
    const candidateId = candidateIdParam || getCandidateId();
    if (!candidateId) {
      return { success: false, error: 'Candidate ID not found', status: 400 };
    }
    // Always use configured base URL
    const url = new URL(API_ENDPOINTS.lastAppliedJobs);
    url.searchParams.append('candidateId', String(candidateId));
    url.searchParams.append('size', String(size || 5));

    console.log('[ApiService] Fetching last applied jobs:', url.toString());
    const response = await apiRequest(url.toString(), { method: 'GET' });
    return response;
  } catch (error) {
    console.error('[ApiService] Error fetching last applied jobs:', error);
    return { success: false, error: error.message || 'Network error', status: 500 };
  }
};

/**
 * Verify Aadhaar via document upload (replaces OTP flow)
 * Sends a multipart/form-data request to legacy verifyAadhaarCard endpoint
 * @param {File} file - Aadhaar document image/PDF
 * @param {string} [aadhaarNumber] - Optional 12-digit Aadhaar number
 * @returns {Promise<{success:boolean, data?:any, error?:string, status?:number}>}
 */
export const verifyAadhaarByUpload = async (file, aadhaarNumber) => {
  try {
    const mobileNumber = getMobileNumber();
    if (!mobileNumber || mobileNumber === '0') {
      return { success: false, error: 'Mobile number is required', status: 400 };
    }

    if (!file) {
      return { success: false, error: 'Aadhaar file is required', status: 400 };
    }

    // Get programmatic token (cached or fetch new)
    const token = await getProgrammaticToken();
    if (!token) {
      return { success: false, error: 'Authentication required', status: 401 };
    }

    const formData = new FormData();
    // Important: do not set Content-Type manually; let the browser add boundaries
    formData.append('file', file, file.name || 'aadhaar');
    // Include mobileNumber to associate request with candidate (per app patterns)
    formData.append('mobileNumber', String(mobileNumber));
    if (aadhaarNumber) {
      formData.append('aadhaarNumber', String(aadhaarNumber));
    }

    const response = await fetch(API_ENDPOINTS.verifyAadhaarCard, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        // Do NOT set 'Content-Type' here
      },
      body: formData,
    });

    const status = response.status;
    let data = null;
    const text = await response.text();
    try {
      data = text ? JSON.parse(text) : null;
    } catch (_) {
      data = { message: text };
    }

    if (response.ok) {
      return { success: true, data, status };
    }
    return { success: false, error: (data && (data.message || data.error)) || text || `HTTP ${status}` , status };
  } catch (error) {
    console.error('[ApiService] Error verifying Aadhaar via upload:', error);
    return { success: false, error: error.message || 'Network error', status: 500 };
  }
};

/**
 * Verify Candidate Aadhaar using the new legacy-services endpoint
 * @param {File} file - Aadhaar document PDF/image
 * @returns {Promise<{success:boolean, data?:any, error?:string, status?:number}>}
 */
export const verifyCandidateAadhaar = async (file) => {
  console.log('[ApiService] verifyCandidateAadhaar called with file:', file);
  
  try {
    const mobileNumber = getMobileNumber();
    console.log('[ApiService] Mobile number from storage:', mobileNumber);
    
    if (!mobileNumber || mobileNumber === '0') {
      console.log('[ApiService] Mobile number validation failed');
      return { success: false, error: 'Mobile number is required', status: 400 };
    }

    if (!file) {
      console.log('[ApiService] File validation failed - no file provided');
      return { success: false, error: 'Aadhaar file is required', status: 400 };
    }

    console.log('[ApiService] File details:', {
      name: file.name,
      size: file.size,
      type: file.type
    });

    // Validate file type - only PDF allowed
    if (file.type !== 'application/pdf') {
      console.log('[ApiService] File type validation failed:', file.type);
      return { success: false, error: 'Only PDF files are allowed', status: 400 };
    }

    console.log('[ApiService] Creating FormData...');
    const formData = new FormData();
    formData.append('file', file, file.name || 'aadhaar');
    formData.append('mobileNumber', String(mobileNumber));

    // Get authentication token
    const token = await getProgrammaticToken();
    if (!token) {
      return { success: false, error: 'Authentication required', status: 401 };
    }

    console.log('[ApiService] FormData created, making API call to legacy-services...');
    console.log('[ApiService] Verifying Candidate Aadhaar:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      mobileNumber
    });

    const response = await fetch(API_ENDPOINTS.verifyCandidateAadhaarByFile, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        // Do NOT set 'Content-Type' here - let browser set it with boundary for multipart/form-data
      },
      body: formData,
    });

    const status = response.status;
    let data = null;
    const text = await response.text();
    
    try {
      data = text ? JSON.parse(text) : null;
    } catch (_) {
      data = { message: text };
    }

    console.log('[ApiService] Candidate Aadhaar verification response:', {
      status,
      data,
      responseText: text
    });

    if (response.ok) {
      // Check if the server response itself indicates failure
      if (data && data.success === false) {
        const errorMessage = data.message || data.error || 'Verification failed';
        return { success: false, error: errorMessage, data, status };
      }
      return { success: true, data, status };
    } else {
      const errorMessage = (data && (data.message || data.error)) || text || `HTTP ${status}`;
      return { success: false, error: errorMessage, status };
    }
  } catch (error) {
    console.error('[ApiService] Error verifying Candidate Aadhaar:', error);
    return { success: false, error: error.message || 'Network error', status: 500 };
  }
};


/**
 * Verify OTP for email-based authentication using channelplayJobs validateOTP endpoint
 * @param {string} email - Email address
 * @param {string} otp - OTP code
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const verifyEmailOtp = async (email, otp) => {
  try {
    const response = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.loginMicroservicePath}channelplayJobs/validateOTP?mobileNumber=&OTP=${encodeURIComponent(otp)}&emailId=${encodeURIComponent(email)}&isClient=true`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({})
    });

    // Handle both JSON and plain text responses
    let data;
    const responseText = await response.text();
    console.log('[API] Validate OTP Response:', { status: response.status, responseText });
    
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      // Response is plain text, not JSON
      data = { message: responseText };
    }
    
    // Check for success message in response data
    if (response.ok || (data && (data.message === 'OTP validated successfully' || data.status === 'success' || responseText.includes('validated') || responseText.includes('success')))) {
      return { success: true, data };
    } else {
      return { success: false, error: data.message || 'Invalid OTP' };
    }
  } catch (error) {
    console.error('OTP verification error:', error);
    return { success: false, error: 'Network error. Please try again.' };
  }
};

/**
 * Fetch client interviews
 * @param {string} mobileNumber - Mobile number
 * @param {string} emailId - Email ID
 * @param {boolean} isComplete - Optional: true for completed interviews, false for pending, undefined for all
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const fetchClientInterviews = async (mobileNumber, emailId, isComplete) => {
  try {
    let baseUrl = API_ENDPOINTS.fetchClientInterviews;
    if (!baseUrl) {
      baseUrl = `${API_CONFIG.baseUrl}${API_CONFIG.loginMicroservicePath}channelplayJobs/fetchClientInterviews`;
    }

    const url = new URL(baseUrl);
    url.searchParams.append('mobileNumber', mobileNumber || '');
    // Normalize emailId: treat undefined/null/'null' as empty string
    const emailParam = (emailId === undefined || emailId === null || String(emailId).toLowerCase() === 'null')
      ? ''
      : String(emailId);
    url.searchParams.append('emailId', emailParam);
    // Always mark caller as client for interview flows
    url.searchParams.append('isClient', 'true');
    url.searchParams.append('isComplete', (isComplete ? 'true' : 'false'));

    console.log('[API] Fetch Client Interviews:', { mobileNumber, emailId, isComplete, url: url.toString() });

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('[API] Fetch Client Interviews Success:', data);

      const normalized = Array.isArray(data)
        ? data.map(item => ({
            ...item,
            resumeFilePath: (item?.resumeFilePath && item.resumeFilePath !== 'null') ? item.resumeFilePath : null,
          }))
        : data;

      return { success: true, data: normalized };
    } else {
      console.error('[API] Fetch Client Interviews Failed:', response.status, response.statusText);
      return { success: false, error: `Failed to fetch interviews: ${response.statusText}` };
    }
  } catch (error) {
    console.error('[API] Fetch Client Interviews Error:', error);
    return { success: false, error: error.message || 'Failed to fetch interviews' };
  }
};

/**
 * Fetch interview feedback data including skills
 * @param {string} interviewId - Interview ID
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const fetchInterviewFeedback = async (interviewId) => {
  try {
    const url = new URL(`${API_CONFIG.baseUrl}${API_CONFIG.loginMicroservicePath}channelplayJobs/fetchInterviewFeedback`);
    url.searchParams.append('interviewId', interviewId || '');
    url.searchParams.append('openingCandidateDetailsId', '0');
    url.searchParams.append('interviewLevel', '0');

    console.log('[API] Fetch Interview Feedback:', { interviewId, url: url.toString() });

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('[API] Fetch Interview Feedback Success:', data);
      return { success: true, data };
    } else {
      console.error('[API] Fetch Interview Feedback Failed:', response.status, response.statusText);
      return { success: false, error: `Failed to fetch interview feedback: ${response.statusText}` };
    }
  } catch (error) {
    console.error('[API] Fetch Interview Feedback Error:', error);
    return { success: false, error: error.message || 'Failed to fetch interview feedback' };
  }
};

/**
 * Submit interview feedback
 * @param {string} interviewId - Interview ID
 * @param {string} candidateStatus - 'Selected', 'Rejected', or 'NoShow'
 * @param {string} interviewFeedback - Feedback comment
 * @param {Object} skillsRatings - Skills ratings object {skillId: rating}
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const submitInterviewFeedback = async (interviewId, candidateStatus, interviewFeedback, skillsRatings) => {
  try {
    const url = new URL(`${API_CONFIG.baseUrl}${API_CONFIG.loginMicroservicePath}channelplayJobs/updateInterviewFeedback`);
    
    // Add query parameters as per Flutter implementation
    url.searchParams.append('interviewId', interviewId);
    url.searchParams.append('candidateStatus', candidateStatus);
    url.searchParams.append('interviewFeedback', interviewFeedback);

    // Get authentication token
    const token = localStorage.getItem('programmaticToken') || localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Authentication token not found');
    }

    console.log('[API] Submit Interview Feedback:', { url: url.toString(), skillsRatings });

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(skillsRatings)
    });

    if (response.ok) {
      // Handle both JSON and plain text responses
      const responseText = await response.text();
      console.log('[API] Submit Interview Feedback Success:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        // Response is plain text, treat as success message
        data = { message: responseText };
      }
      
      return { success: true, data, message: responseText };
    } else {
      console.error('[API] Submit Interview Feedback Failed:', response.status, response.statusText);
      return { success: false, error: `Failed to submit interview feedback: ${response.statusText}` };
    }
  } catch (error) {
    console.error('[API] Submit Interview Feedback Error:', error);
    return { success: false, error: error.message || 'Failed to submit interview feedback' };
  }
};

/**
 * Fetches the actual URL (plain text) from backend URL shortener endpoint
 * @returns {Promise<{success: boolean, data?: string, error?: string, status?: number}>}
 */
export const getActualUrl = async (inputUrl) => {
  try {
    // Build endpoint with required 'url' query parameter
    const u = new URL(API_ENDPOINTS.getActualUrl);
    const currentUrl = inputUrl || (typeof window !== 'undefined' ? window.location.href : '');

    // Extract only the last path segment (route name) without query/hash
    let routeName = '';
    if (currentUrl) {
      try {
        // Handle both full URLs and relative paths dynamically
        let parsed;
        if (currentUrl.startsWith('http://') || currentUrl.startsWith('https://')) {
          // Full URL - parse directly
          parsed = new URL(currentUrl);
        } else if (typeof window !== 'undefined' && window.location?.origin) {
          // Relative path - use current origin
          parsed = new URL(currentUrl, window.location.origin);
        } else {
          // Fallback: treat as pathname and extract segments directly
          throw new Error('No base URL available');
        }
        const segments = (parsed.pathname || '/').split('/').filter(Boolean);
        routeName = segments.length ? segments[segments.length - 1] : '';
      } catch (_) {
        // Fallback: treat input as a pathname and extract segments directly
        const path = String(currentUrl);
        const segments = path.split('?')[0].split('#')[0].split('/').filter(Boolean);
        routeName = segments.length ? segments[segments.length - 1] : '';
      }
    }

    if (routeName) {
      u.searchParams.append('url', routeName);
    }

    const response = await fetch(u.toString(), {
      method: 'GET',
      headers: {
        'Accept': 'text/plain, */*'
      }
    });

    const status = response.status;
    const text = await response.text();

    if (response.ok) {
      return { success: true, data: text, status };
    }

    return { success: false, error: text || `HTTP ${status}` , status };
  } catch (error) {
    return { success: false, error: error.message || 'Network error', status: 500 };
  }
};

/**
 * Geocode an address/pincode using backend proxy (channelplayJobs/geocode)
 * @param {string} address - Address or pincode to geocode
 * @returns {Promise<{success: boolean, data?: {latitude:number, longitude:number}, status?: number, error?: string}>}
 */
export const geocodeAddress = async (address) => {
  try {
    if (!address || !String(address).trim()) {
      return { success: false, status: 400, error: 'address is required' };
    }
    const url = new URL(API_ENDPOINTS.geocodeAddress);
    url.searchParams.append('address', String(address).trim());
    const response = await apiRequest(url.toString(), { method: 'GET' });
    if (!response.success || !response.data) return response;

    const data = response.data;
    if (data.status === 'OK' && Array.isArray(data.results) && data.results[0]?.geometry?.location) {
      const loc = data.results[0].geometry.location;
      const latitude = Number(loc.lat);
      const longitude = Number(loc.lng);
      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        return { success: true, data: { latitude, longitude }, status: response.status };
      }
    }
    return { success: false, error: data.status || 'Geocoding failed', status: response.status };
  } catch (error) {
    return { success: false, error: error.message || 'Network error', status: 500 };
  }
};

/**
 * Generic API request function
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options
 * @returns {Promise} - API response
 */
const apiRequest = async (url, options = {}) => {
  const defaultOptions = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    timeout: API_CONFIG.defaultTimeout,
  };

  // Build initial config
  const config = { ...defaultOptions, ...options };

  // Inject Authorization header from cached token unless caller explicitly sets Authorization
  try {
    const isTokenEndpoint = typeof url === 'string' && url.includes(API_ENDPOINTS.userToken);
    const hasAuthHeader = !!(config.headers && (config.headers.Authorization || config.headers.authorization));
    if (!isTokenEndpoint && !hasAuthHeader) {
      const cachedToken = localStorage.getItem('programmaticToken');
      if (cachedToken) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${cachedToken}`,
        };
      }
    }
  } catch (_) {
    // ignore storage access issues
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.timeout);

    const doFetch = async () => fetch(url, {
      ...config,
      signal: controller.signal,
    });
    let response = await doFetch();

    clearTimeout(timeoutId);

    // Handle different status codes like Flutter implementation
    if (response.status === 200) {
      const data = await response.json();
      return {
        success: true,
        data,
        status: response.status,
      };
    } else if (response.status === 404) {
      // Handle 404 as a valid response (candidate not found)
      return {
        success: true,
        data: null,
        status: response.status,
      };
    } else if (response.status === 401) {
      // Attempt a single retry after refreshing the token
      try {
        // Avoid recursion when the request itself is token endpoint
        const isTokenEndpoint = typeof url === 'string' && url.includes(API_ENDPOINTS.userToken);
        if (!isTokenEndpoint) {
          // Mark current token as stale but do not clear it aggressively
          try {
            localStorage.removeItem('programmaticTokenTimestamp');
          } catch (_) {}
          // Fetch fresh token
          const refreshed = await fetchProgrammaticUser();
          if (refreshed && refreshed.success) {
            const newToken = (refreshed.data && (refreshed.data.token || refreshed.data)) || localStorage.getItem('programmaticToken');
            if (newToken) {
              // Update Authorization header and retry once
              config.headers = {
                ...config.headers,
                Authorization: `Bearer ${newToken}`,
              };
              const retryController = new AbortController();
              const retryTimeoutId = setTimeout(() => retryController.abort(), config.timeout);
              response = await fetch(url, { ...config, signal: retryController.signal });
              clearTimeout(retryTimeoutId);
              if (response.status === 200) {
                const data = await response.json();
                return { success: true, data, status: response.status };
              }
            }
          }
        }
      } catch (e) {
        // fall through to generic error handling
      }
      const errorText = await response.text();
      return {
        success: false,
        error: `HTTP ${response.status}: ${errorText}`,
        status: response.status,
      };
    } else {
      // Handle other error status codes
      const errorText = await response.text();
      return {
        success: false,
        error: `HTTP ${response.status}: ${errorText}`,
        status: response.status,
      };
    }
  } catch (error) {
    console.error('API Request Error:', error);
    return {
      success: false,
      error: error.message,
      status: error.name === 'AbortError' ? 408 : 500,
    };
  }
};

/**
 * Fetch candidate timeline events for a given openingCandidateDetailId
 * Backend endpoint: recruiter-services/api/v1/positions/candidate-timeline/{openingCandidateDetailId}
 * @param {string|number} openingCandidateDetailId
 * @returns {Promise<{success:boolean, data?:any, error?:string, status?:number}>}
 */
export const getCandidateTimeline = async (openingCandidateDetailId) => {
  try {
    const id = String(openingCandidateDetailId || '').trim();
    if (!id) {
      return { success: false, error: 'openingCandidateDetailId is required', status: 400 };
    }

    const url = `${API_ENDPOINTS.fetchCandidateTimeline}${encodeURIComponent(id)}`;
    console.log('[ApiService] Fetching candidate timeline:', url);

    // Use generic request helper (injects token if available and handles 401 retry)
    const resp = await apiRequest(url, { method: 'GET' });
    return resp;
  } catch (error) {
    console.error('[ApiService] Error fetching candidate timeline:', error);
    return { success: false, error: error.message || 'Network error', status: 500 };
  }
};

/**
 * Unified job search function that handles both regular and filtered searches
 * Uses the new searchJobs endpoint that combines fetchJobsByCoordinates and fetchJobsWithFilters
 * @param {Object} options - Search options
 * @param {number} options.latitude - User's latitude
 * @param {number} options.longitude - User's longitude
 * @param {number} [options.distance=100] - Search distance in km
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.size=10] - Number of jobs per page
 * @param {string} [options.mobileNumber] - User's mobile number (for logged-in users)
 * @param {Array<number>} [options.skillIds] - Array of skill IDs for filtering
 * @param {Array<number>} [options.designationIds] - Array of designation IDs for filtering
 * @param {Array<string>} [options.keywords] - Array of keywords for job description search
 * @returns {Promise<{success: boolean, data?: any, error?: string, status?: number}>}
 */
export const searchJobs = async (options = {}) => {
  try {
    const {
      latitude,
      longitude,
      distance = 100,
      page = 1,
      size = 10,
      mobileNumber = null,
      skillIds = [],
      designationIds = [],
      keywords = []
    } = options;

    // Validate required parameters
    if (!latitude || !longitude) {
      return { success: false, error: 'Latitude and longitude are required', status: 400 };
    }

    // Get effective mobile number from storage if not provided
    const effectiveMobile = mobileNumber || getMobileNumber();
    
    // Build request body based on user authentication and filters
    const requestBody = {
      latitude: Number(latitude),
      longitude: Number(longitude),
      distance: Number(distance),
      page: Number(page),
      size: Number(size)
    };

    // Add mobile number for all users (including non-authenticated with '0')
    if (effectiveMobile) {
      requestBody.mobileNumber = effectiveMobile;
    } else {
      // Fallback to '0' for non-authenticated users
      requestBody.mobileNumber = '0';
    }

    // Add filters if provided
    if (Array.isArray(skillIds) && skillIds.length > 0) {
      requestBody.skillIds = skillIds.map(id => Number(id));
    }

    if (Array.isArray(designationIds) && designationIds.length > 0) {
      requestBody.designationIds = designationIds.map(id => Number(id));
    }

    // Add keywords for job description search
    if (Array.isArray(keywords) && keywords.length > 0) {
      requestBody.keywords = keywords.map(kw => String(kw).trim()).filter(Boolean);
    }

    console.log('[ApiService] Unified job search request:', requestBody);
    console.log('[ApiService] Endpoint:', API_ENDPOINTS.searchJobs);

    // Get authentication token for the request
    const authToken = await getProgrammaticToken();
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add authorization header if token is available
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(API_ENDPOINTS.searchJobs, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody)
    });

    const status = response.status;
    let data = null;

    try {
      const text = await response.text();
      data = text ? JSON.parse(text) : null;
    } catch (e) {
      const text = await response.text();
      data = { message: text };
    }

    if (response.ok) {
      console.log('[ApiService] Unified job search success:', data);
      return { success: true, data, status };
    } else {
      console.error('[ApiService] Unified job search failed:', status, data);
      return { success: false, error: data?.message || `HTTP ${status}`, status };
    }

  } catch (error) {
    console.error('[ApiService] Error in unified job search:', error);
    return { success: false, error: error.message || 'Network error', status: 500 };
  }
};

/**
 * Fetch jobs by coordinates with pagination - LEGACY FUNCTION (use searchJobs instead)
 * @param {number} latitude - User's latitude
 * @param {number} longitude - User's longitude
 * @param {number} page - Page number (default: 1)
 * @param {number} size - Number of jobs per page (default: 10)
 * @param {string} language - App language (default: 'en')
 * @param {string} mobileNumber - User's mobile number (required)
 * @param {string} searchTerm - Optional search term
 * @param {string} radius - Optional search radius
 * @param {string} token - Authentication token
 * @returns {Promise} - Jobs data
 */
export const fetchJobsByCoordinates = async ({
  latitude,
  longitude,
  page = 1,
  size = 10,
  language = null,
  mobileNumber,
  searchTerm = null,
  radius = null,
  token = null
}) => {
  // Use the new unified searchJobs function
  console.log(`[ApiService] LEGACY: fetchJobsByCoordinates called, redirecting to searchJobs`);
  
  const distance = radius ? Number(radius) : 100; // Convert radius to distance
  
  return searchJobs({
    latitude,
    longitude,
    distance,
    page,
    size,
    mobileNumber
  });
};

/**
 * Fetch jobs with filters - LEGACY FUNCTION (use searchJobs instead)
 * Updated to use the new unified searchJobs endpoint with skillIds instead of skill names
 * @param {Object} options - Configuration options
 * @param {number} options.latitude - Latitude coordinate
 * @param {number} options.longitude - Longitude coordinate
 * @param {number} options.radius - Search radius in km (converted to distance)
 * @param {Array<number>} options.skillIds - Skill IDs array (updated from skill names)
 * @param {Array<number>} options.designationIds - Designation IDs array (updated from designation names)
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.size - Page size (default: 100)
 * @param {string} options.mobileNumber - Mobile number
 * @returns {Promise<{success:boolean, data?:any, error?:string}>}
 */
export const fetchJobsWithFilters = async (options = {}) => {
  console.log('[ApiService] LEGACY: fetchJobsWithFilters called, redirecting to searchJobs');
  
  const {
    latitude = 28.4594965,
    longitude = 77.0266383,
    radius = 150,
    skillIds = [],
    designationIds = [],
    page = 1,
    size = 100,
    mobileNumber = null,
    // Legacy support for old parameter names
    skills = [],
    designations = []
  } = options;

  // Convert legacy skills/designations arrays to IDs if needed
  // Note: This assumes the legacy arrays contain objects with 'id' property
  // or are already arrays of IDs
  let effectiveSkillIds = skillIds;
  let effectiveDesignationIds = designationIds;

  if (skills.length > 0 && skillIds.length === 0) {
    effectiveSkillIds = skills.map(skill => 
      typeof skill === 'object' && skill.id ? skill.id : skill
    ).filter(id => id != null);
  }

  if (designations.length > 0 && designationIds.length === 0) {
    effectiveDesignationIds = designations.map(designation => 
      typeof designation === 'object' && designation.id ? designation.id : designation
    ).filter(id => id != null);
  }

  // Use the new unified searchJobs function
  return searchJobs({
    latitude,
    longitude,
    distance: radius, // Convert radius to distance
    page,
    size,
    mobileNumber,
    skillIds: effectiveSkillIds,
    designationIds: effectiveDesignationIds
  });
};

/**
 * Get user's current location using browser geolocation API
 * @returns {Promise} - Location coordinates
 */
export const getCurrentLocation = (forceFresh = false) => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: forceFresh ? 0 : 300000, // 0 for fresh request, 5 minutes for cached
    };
    
    console.log('[ApiService] getCurrentLocation called with options:', options);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        let errorMessage = 'Unable to retrieve location';
        let errorType = 'UNKNOWN_ERROR';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user';
            errorType = 'PERMISSION_DENIED';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            errorType = 'POSITION_UNAVAILABLE';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            errorType = 'TIMEOUT';
            break;
          default:
            errorMessage = 'Unknown location error';
            errorType = 'UNKNOWN_ERROR';
            break;
        }
        const locationError = new Error(errorMessage);
        locationError.type = errorType;
        reject(locationError);
      },
      options
    );
  });
};

/**
 * Get location from IP address using ipapi.co service
 * @returns {Promise} - Location coordinates from IP
 */
export const getLocationFromIP = async () => {
  try {
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.latitude && data.longitude) {
      return {
        latitude: parseFloat(data.latitude),
        longitude: parseFloat(data.longitude),
        accuracy: 10000, // IP-based location is less accurate
        source: 'ip',
        city: data.city,
        region: data.region,
        country: data.country_name
      };
    } else {
      throw new Error('No coordinates in IP geolocation response');
    }
  } catch (error) {
    console.error('IP geolocation failed:', error);
    throw new Error('Failed to get location from IP address');
  }
};

/**
 * Get pincode from OpenStreetMap Nominatim API
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @returns {Promise<{cityName: string, pincode: string}>}
 */
const getPincodeFromNominatim = async (latitude, longitude) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000);
  
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1&accept-language=en&zoom=18`;
    const response = await fetch(url, {
      headers: { 'Accept': 'application/json', 'Accept-Language': 'en' },
      signal: controller.signal,
      mode: 'cors',
      referrerPolicy: 'no-referrer'
    });
    clearTimeout(timeoutId);
    
    if (!response.ok) throw new Error(`Nominatim failed: ${response.status}`);
    
    const data = await response.json();
    const address = data.address || {};
    const cityName = address.city || address.town || address.village || address.municipality || 
                    address.county || address.state_district || address.state || '';
    const pincode = address.postcode || '';
    
    console.log('[ApiService] Nominatim result:', { cityName, pincode });
    return { cityName, pincode, source: 'nominatim' };
  } catch (error) {
    clearTimeout(timeoutId);
    console.warn('[ApiService] Nominatim failed:', error.message);
    return { cityName: '', pincode: '', source: 'nominatim' };
  }
};

/**
 * Get pincode from BigDataCloud API
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @returns {Promise<{cityName: string, pincode: string}>}
 */
const getPincodeFromBigDataCloud = async (latitude, longitude) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000);
  
  try {
    const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`;
    const response = await fetch(url, {
      signal: controller.signal,
      mode: 'cors',
      headers: { 'Accept': 'application/json' }
    });
    clearTimeout(timeoutId);
    
    if (!response.ok) throw new Error(`BigDataCloud failed: ${response.status}`);
    
    const data = await response.json();
    const cityName = data.city || data.locality || data.principalSubdivision || '';
    const pincode = data.postcode || '';
    
    console.log('[ApiService] BigDataCloud result:', { cityName, pincode });
    return { cityName, pincode, source: 'bigdatacloud' };
  } catch (error) {
    clearTimeout(timeoutId);
    console.warn('[ApiService] BigDataCloud failed:', error.message);
    return { cityName: '', pincode: '', source: 'bigdatacloud' };
  }
};

/**
 * Get pincode from backend Google Maps proxy (uses formatted address for reverse lookup)
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @returns {Promise<{cityName: string, pincode: string}>}
 */
const getPincodeFromGoogleProxy = async (latitude, longitude) => {
  try {
    // Use the backend geocode endpoint with lat,lng as address (Google accepts this format)
    const url = new URL(API_ENDPOINTS.geocodeAddress);
    url.searchParams.append('address', `${latitude},${longitude}`);
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    });
    
    if (!response.ok) throw new Error(`Google proxy failed: ${response.status}`);
    
    const data = await response.json();
    console.log('[ApiService] Google proxy raw response:', data);
    
    if (data.status === 'OK' && Array.isArray(data.results) && data.results.length > 0) {
      const result = data.results[0];
      let cityName = '';
      let pincode = '';
      
      if (result.address_components) {
        for (const component of result.address_components) {
          const types = component.types;
          if (types.includes('locality') && !cityName) {
            cityName = component.long_name;
          }
          if (types.includes('administrative_area_level_2') && !cityName) {
            cityName = component.long_name;
          }
          if (types.includes('postal_code')) {
            pincode = component.long_name;
          }
        }
      }
      
      console.log('[ApiService] Google proxy result:', { cityName, pincode });
      return { cityName, pincode, source: 'google' };
    }
    
    return { cityName: '', pincode: '', source: 'google' };
  } catch (error) {
    console.warn('[ApiService] Google proxy failed:', error.message);
    return { cityName: '', pincode: '', source: 'google' };
  }
};

/**
 * Backup geocoding service using BigDataCloud - returns both city name and pincode
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @returns {Promise<{cityName: string, pincode: string}>} - City name and pincode
 */
const getBackupLocationDetails = async (latitude, longitude) => {
  return await getPincodeFromBigDataCloud(latitude, longitude);
};

/**
 * Backup geocoding service - returns only city name (for backward compatibility)
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @returns {Promise<string>} - City name
 */
const getBackupCityName = async (latitude, longitude) => {
  const details = await getBackupLocationDetails(latitude, longitude);
  return details.cityName;
};

/**
 * Get location details (city name and pincode) from coordinates using multiple APIs in parallel
 * Tries Nominatim, BigDataCloud, and Google backend proxy simultaneously for reliability
 * @param {number} latitude 
 * @param {number} longitude 
 * @returns {Promise<{cityName: string, pincode?: string}>}
 */
export const getLocationDetailsFromCoordinates = async (latitude, longitude) => {
  console.log('[ApiService] ========== REVERSE GEOCODING START (PARALLEL) ==========');
  console.log('[ApiService] Getting location details for coordinates:', { latitude, longitude });
  
  try {
    // Call all three APIs in parallel for maximum reliability
    const [nominatimResult, bigDataCloudResult, googleResult] = await Promise.all([
      getPincodeFromNominatim(latitude, longitude),
      getPincodeFromBigDataCloud(latitude, longitude),
      getPincodeFromGoogleProxy(latitude, longitude)
    ]);
    
    console.log('[ApiService] All API results:', { nominatimResult, bigDataCloudResult, googleResult });
    
    // Determine best city name (prefer Nominatim > BigDataCloud > Google)
    let cityName = nominatimResult.cityName || bigDataCloudResult.cityName || googleResult.cityName || '';
    
    // Determine best pincode (prefer whichever has one)
    let pincode = '';
    let pincodeSource = '';
    
    if (nominatimResult.pincode) {
      pincode = nominatimResult.pincode;
      pincodeSource = 'nominatim';
    } else if (bigDataCloudResult.pincode) {
      pincode = bigDataCloudResult.pincode;
      pincodeSource = 'bigdatacloud';
    } else if (googleResult.pincode) {
      pincode = googleResult.pincode;
      pincodeSource = 'google';
    }
    
    console.log('[ApiService] ========== REVERSE GEOCODING SUCCESS ==========');
    console.log('[ApiService] Final result:', { cityName, pincode, pincodeSource });
    
    // If we got a city name, return the result
    if (cityName) {
      return { cityName, pincode };
    }
    
    // Fallback to coordinates if no city name found
    const fallbackName = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
    console.log('[ApiService] Using coordinate fallback:', fallbackName);
    return { cityName: fallbackName, pincode: '' };
  } catch (error) {
    console.error('[ApiService] All reverse geocoding methods failed:', error);
    const fallbackName = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
    return { cityName: fallbackName, pincode: '' };
  }
};

/**
 * Get location details (city name and pincode) from coordinates using OpenStreetMap (backup)
 * @param {number} latitude 
 * @param {number} longitude 
 * @returns {Promise<{cityName: string, pincode?: string}>}
 */
export const getLocationDetailsFromCoordinatesOSM = async (latitude, longitude) => {
  try {
    console.log('[ApiService] Starting reverse geocoding for coordinates:', { latitude, longitude });
    
    // Primary service: OpenStreetMap Nominatim API with better headers for production
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1&accept-language=en&zoom=10`;
    console.log('[ApiService] Primary reverse geocoding URL:', url);
    
    // Create timeout controller for better browser compatibility
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    const response = await fetch(url, {
      headers: {
        'Accept': 'application/json',
        'Accept-Language': 'en'
      },
      signal: controller.signal,
      mode: 'cors',
      cache: 'default',
      referrerPolicy: 'no-referrer'
    });
    
    clearTimeout(timeoutId);
    
    console.log('[ApiService] Primary reverse geocoding response status:', response.status, response.statusText);
    
    if (!response.ok) {
      throw new Error(`Primary reverse geocoding HTTP error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('[ApiService] Primary reverse geocoding raw response:', data);
    
    // Check if response has valid data
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from primary reverse geocoding API');
    }
    
    // Extract city name and pincode from the response
    const address = data.address || {};
    const cityName = address.city || 
                    address.town || 
                    address.village || 
                    address.municipality || 
                    address.county || 
                    address.state_district || 
                    address.state || 
                    null;
    
    const pincode = address.postcode || '';
    
    // Validate that we got a meaningful city name
    if (!cityName) {
      console.warn('[ApiService] No valid city name found in primary response, trying backup service');
      throw new Error('No city name from primary service');
    }
    
    console.log('[ApiService] Primary reverse geocoding successful:', { latitude, longitude, cityName, pincode, fullAddress: address });
    return { cityName, pincode };
    
  } catch (primaryError) {
    console.warn('[ApiService] Primary reverse geocoding failed:', primaryError.message);
    
    // Try backup geocoding service
    try {
      const backupCityName = await getBackupCityName(latitude, longitude);
      return { cityName: backupCityName, pincode: '' };
    } catch (backupError) {
      console.error('[ApiService] Both primary and backup reverse geocoding failed');
      
      // Final fallback to coordinates display
      const fallbackName = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
      console.log('[ApiService] Using coordinate fallback:', fallbackName);
      return { cityName: fallbackName, pincode: '' };
    }
  }
};

export const getCityNameFromCoordinates = async (latitude, longitude) => {
  // Delegate to the new function and return only the city name
  const locationDetails = await getLocationDetailsFromCoordinates(latitude, longitude);
  return locationDetails.cityName;
};

/**
 * Get default location (fallback when geolocation fails)
 * Using Delhi coordinates as default
 * @returns {object} - Default location coordinates
 */
export const getDefaultLocation = () => {
  return {
    latitude: 28.6139,
    longitude: 77.2090,
    city: null, // Don't hardcode city name, let reverse geocoding determine it
    isDefault: true,
  };
};

/**
 * Get mobile number from localStorage
 * @returns {string} - Mobile number
 */
export const getMobileNumber = () => {
  // Check multiple possible keys for mobile number storage
  return localStorage.getItem('userMobileNumber') || 
         localStorage.getItem('mobileNumber') || 
         '0';
};

// Global token fetching state to prevent concurrent calls
let isTokenFetching = false;
let tokenFetchPromise = null;

/**
 * Fetch programmatic user data from the API
 * This should be called first when accessing the profile screen
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const fetchProgrammaticUser = async () => {
  try {
    console.log('[ApiService] 🔄 Calling fetchProgrammaticUser API...');
    
    const response = await fetch(API_ENDPOINTS.userToken, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('[ApiService] ✅ fetchProgrammaticUser API response:', data);
      
      // Cache the token if it exists in the response
      const token = data.token || data;
      if (token) {
        localStorage.setItem('programmaticToken', token);
        localStorage.setItem('programmaticTokenTimestamp', Date.now().toString());
        console.log('[ApiService] ✅ Programmatic token cached from fetchProgrammaticUser');
      }
      
      return { success: true, data };
    } else {
      console.error('[ApiService] ❌ fetchProgrammaticUser API failed:', response.status, response.statusText);
      return { success: false, error: `HTTP ${response.status}: ${response.statusText}` };
    }
  } catch (error) {
    console.error('[ApiService] ❌ fetchProgrammaticUser API error:', error);
    return { success: false, error: error.message || 'Network error' };
  }
};


/**
 * Get programmatic token from localStorage (cached by Profile component)
 * Prevents multiple concurrent API calls
 * @returns {Promise<string|null>} - Authentication token
 */
export const getProgrammaticToken = async () => {
  // Check if token exists in localStorage (cached by Profile component)
  const storedToken = localStorage.getItem('programmaticToken');
  const tokenTimestamp = localStorage.getItem('programmaticTokenTimestamp');
  
  if (storedToken && tokenTimestamp) {
    const now = Date.now();
    const tokenAge = now - parseInt(tokenTimestamp);
    const maxAge = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
    
    if (tokenAge < maxAge) {
      console.log('[ApiService] Using cached programmatic token');
      return storedToken;
    } else {
      console.log('[ApiService] Cached token expired, clearing cache');
      // Clear expired token
      localStorage.removeItem('programmaticToken');
      localStorage.removeItem('programmaticTokenTimestamp');
    }
  }

  // If already fetching, wait for the existing promise
  if (isTokenFetching && tokenFetchPromise) {
    console.log('[ApiService] Token fetch already in progress, waiting...');
    return await tokenFetchPromise;
  }

  // If no valid cached token, try to fetch it (fallback)
  const mobileNumber = getMobileNumber();
  if (!mobileNumber || mobileNumber === '0') {
    console.error('[ApiService] No mobile number available and no cached token');
    return null;
  }

  // Set fetching state and create promise
  isTokenFetching = true;
  tokenFetchPromise = (async () => {
    try {
      console.log('[ApiService] Fetching new programmatic token as fallback');
      const tokenResponse = await apiRequest(API_ENDPOINTS.userToken, {
        method: 'GET'
      });
      
      if (tokenResponse.success) {
        const token = tokenResponse.data.token || tokenResponse.data;
        
        // Store token and timestamp in localStorage
        const now = Date.now();
        localStorage.setItem('programmaticToken', token);
        localStorage.setItem('programmaticTokenTimestamp', now.toString());
        
        console.log('[ApiService] New programmatic token cached successfully');
        return token;
      } else {
        console.error('[ApiService] Failed to fetch programmatic token:', tokenResponse.error);
        return null;
      }
    } catch (error) {
      console.error('[ApiService] Error fetching programmatic token:', error);
      return null;
    } finally {
      // Reset fetching state
      isTokenFetching = false;
      tokenFetchPromise = null;
    }
  })();

  return await tokenFetchPromise;
};

/**
 * Initialize programmatic token once on app load.
 * - Uses cached token if younger than 8 hours.
 * - Otherwise calls fetchProgrammaticUser to refresh and cache.
 */
export const initProgrammaticToken = async () => {
  try {
    const token = localStorage.getItem('programmaticToken');
    const ts = localStorage.getItem('programmaticTokenTimestamp');
    const now = Date.now();
    const isFresh = token && ts && (now - parseInt(ts)) < (8 * 60 * 60 * 1000);
    if (isFresh) {
      return { success: true, token };
    }
    const resp = await fetchProgrammaticUser();
    if (resp && resp.success) {
      const newToken = resp.data?.token || resp.data || localStorage.getItem('programmaticToken');
      return { success: true, token: newToken };
    }
    return resp || { success: false, error: 'Failed to initialize token' };
  } catch (e) {
    return { success: false, error: 'Token initialization failed' };
  }
};

/**
 * Save/Update candidate details before applying for job
 * Gets all existing candidate data from getCandidateByMobile API and only updates the email field
 * @param {object} candidateData - Candidate data with email field to update
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const saveCandidateDetails = async (candidateData) => {
  try {
    console.log('[ApiService] saveCandidateDetails called with:', candidateData);
    console.log('[ApiService] Getting existing candidate data and only updating email field');
    
    // Step 1: Get all existing candidate data from getCandidateByMobile API
    const existingDataResult = await getCandidateByMobile();
    console.log('[ApiService] Existing candidate data result:', existingDataResult);
    
    if (!existingDataResult.success) {
      console.error('[ApiService] Failed to get existing candidate data:', existingDataResult.error);
      return {
        success: false,
        error: `Failed to get existing candidate data: ${existingDataResult.error}`
      };
    }
    
    const existingProfile = existingDataResult.data || {};
    console.log('[ApiService] Existing profile data:', existingProfile);
    
    // Step 2: Extract only specific values from getCandidateByMobile API and map to correct keys
    const profileData = {
      fullName: existingProfile.fullName || '',
      email: candidateData.email || '', // Only field that gets updated from text input
      expectedSalary: existingProfile.expectedSalary || '',
      city: existingProfile.city || '',
      pincode: existingProfile.pincode || ''
    };
    
    console.log('[ApiService] Profile data with existing values + updated email:', profileData);
    console.log('[ApiService] Email field updated from:', existingProfile.email, 'to:', candidateData.email);
    
    // Step 3: Call updateUserProfile with the complete data (only email changed)
    const result = await updateUserProfile(profileData);
    
    console.log('[ApiService] updateUserProfile result:', result);
    
    // Return result in the expected format
    if (result.success) {
      return {
        success: true,
        data: result.data,
        message: result.message || 'Candidate details saved successfully'
      };
    } else {
      return {
        success: false,
        error: result.error || 'Failed to save candidate details',
        data: result.data
      };
    }
    
  } catch (error) {
    console.error('[ApiService] Save candidate details exception:', error);
    return {
      success: false,
      error: `Error saving candidate details: ${error.message}`
    };
  }
};

/**
 * Apply for a job with optional resume file
 * @param {string} positionId - Position ID
 * @param {object} applicationData - Application data including resumeFile
 * @returns {Promise} - Application result with applicationId and candidateStatus
 */
export const applyForJob = async (positionId, applicationData = {}) => {
  try {
    console.log('[ApiService] applyForJob called with:', { positionId, applicationData });
    
    const mobileNumber = getMobileNumber();
    console.log('[ApiService] Mobile number from storage:', mobileNumber);
    
    if (!positionId) {
      console.error('[ApiService] ERROR: Position ID is required for application');
      return {
        success: false,
        message: 'Position ID is required to apply for a job'
      };
    }
    
    // Build URL with query parameters matching V2 implementation
    const candidateName = applicationData.fullName || 'User';
    const recruiterId = (applicationData.recruiterId !== undefined && applicationData.recruiterId !== null && applicationData.recruiterId !== '')
      ? String(applicationData.recruiterId)
      : '0'; // Default value as per V2
    
    const url = new URL(API_ENDPOINTS.applyForJob);
    url.searchParams.append('mobileNumber', mobileNumber);
    url.searchParams.append('positionId', String(positionId));
    url.searchParams.append('name', candidateName);
    url.searchParams.append('recruiterId', recruiterId);
    
    console.log('[ApiService] Apply for job URL:', url.toString());
    console.log('[ApiService] Using recruiterId:', recruiterId);
    console.log('[ApiService] Has resume file:', !!applicationData.resumeFile);
    
    // Create FormData for multipart request (matching V2)
    const formData = new FormData();
    
    // Add resume file if provided
    if (applicationData.resumeFile) {
      formData.append('resume', applicationData.resumeFile, applicationData.resumeFile.name);
      console.log('[ApiService] Resume added to request:', applicationData.resumeFile.name);
    }
    
    console.log('[ApiService] Sending multipart request');
    
    // Use fetch directly for multipart/form-data
    const response = await fetch(url.toString(), {
      method: 'POST',
      body: formData,
      // Don't set Content-Type header, let browser set it with boundary
    });
    
    console.log('[ApiService] Apply job response status:', response.status);
    
    let responseData = {};
    const responseText = await response.text();
    console.log('[ApiService] Apply job response body:', responseText);
    
    if (responseText) {
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.log('[ApiService] Response is not JSON, treating as text');
        responseData = { message: responseText };
      }
    }
    
    // Success detection matching V2 (any 200 status code)
    const success = response.status === 200;
    console.log('[ApiService] Application success detected:', success);
    
    if (success) {
      // Generate application ID similar to V2
      const appIdValue = responseData.id;
      const applicationId = appIdValue ? appIdValue.toString() : `app-${Date.now()}`;
      
      return {
        success: true,
        applicationId: applicationId,
        candidateStatus: responseData.candidateStatus || 'submitted',
        message: responseData.message || 'Successfully applied for job',
        data: responseData
      };
    } else {
      console.log('[ApiService] Failed to apply for job:', response.status);
      
      // Extract user-friendly error message from backend response
      let errorMessage = 'Failed to submit application. Please try again.';
      if (response.status === 400) {
        // Handle position-related errors with user-friendly messages
        const backendMessage = responseData.message || responseText || '';
        if (backendMessage.includes('position is inactive') || backendMessage.includes('position is closed')) {
          errorMessage = 'This position is no longer accepting applications.';
        } else if (backendMessage.includes('no vacant positions')) {
          errorMessage = 'Sorry, all positions have been filled for this job.';
        } else if (backendMessage.includes('Cannot add candidate')) {
          errorMessage = 'This position is currently not available for applications.';
        } else if (backendMessage) {
          errorMessage = backendMessage;
        }
      }
      
      return {
        success: false,
        message: errorMessage,
        data: responseData
      };
    }
    
  } catch (error) {
    console.error('[ApiService] Apply for job exception:', error);
    console.error('[ApiService] Exception details:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
    return {
      success: false,
      message: `Error applying for job: ${error.message}`
    };
  }
};

/**
 * Get job details by ID
 * @param {string} jobId - Job ID
 * @returns {Promise} - Job details
 */
export const getJobById = async (jobId) => {
  const mobileNumber = getMobileNumber();
  
  const url = new URL(API_ENDPOINTS.fetchJobById);
  url.searchParams.append('jobId', jobId);
  url.searchParams.append('mobileNumber', mobileNumber);

  console.log(`[ApiService] Fetching job details for ID: ${jobId}`);
  
  return apiRequest(url.toString(), {
    method: 'GET',
  });
};

/**
 * Get user applications
 * Backward-compatible signature with optional language and filter options.
 * @param {number} page - Page number
 * @param {number} size - Page size
 * @param {string} [language='en'] - Language code
 * @param {{status?: string, sortBy?: string, dateRange?: string, fromDate?: string, toDate?: string}} [filters={}] - Optional filters
 * @returns {Promise} - Applications data
 */
export const getApplications = async (page = 1, size = 50, language = 'en', filters = {}) => {
  const mobileNumber = getMobileNumber();

  const url = new URL(API_ENDPOINTS.fetchCandidateApplications);
  url.searchParams.append('mobileNumber', mobileNumber);
  url.searchParams.append('page', page.toString());
  url.searchParams.append('size', size.toString());
  url.searchParams.append('language', language || 'en');

  // Optional backend-supported filters (defensive: only append if provided)
  if (filters && typeof filters === 'object') {
    const { status, sortBy, dateRange, fromDate, toDate } = filters;
    if (status) url.searchParams.append('status', String(status));
    if (sortBy) url.searchParams.append('sortBy', String(sortBy));
    if (dateRange) url.searchParams.append('dateRange', String(dateRange));
    if (fromDate) url.searchParams.append('fromDate', String(fromDate));
    if (toDate) url.searchParams.append('toDate', String(toDate));
  }

  console.log('[ApiService] Fetching applications with params:', {
    mobileNumber,
    page,
    size,
    language: language || 'en',
    filters,
    url: url.toString(),
  });

  return apiRequest(url.toString(), {
    method: 'GET',
  });
};

/**
 * Get user referrals with stats
 * @returns {Promise} - Referrals data with statistics
 */
export const getReferrals = async () => {
  const mobileNumber = getMobileNumber();
  
  const url = new URL(API_ENDPOINTS.fetchReferrals);
  url.searchParams.append('mobileNumber', mobileNumber);

  console.log(`[ApiService] Fetching referrals for mobile: ${mobileNumber}`);
  console.log(`[ApiService] URL: ${url.toString()}`);
  
  try {
    const response = await apiRequest(url.toString(), {
      method: 'GET',
    });
    
    console.log('[ApiService] Referrals response received:', response);
    
    // Initialize default values
    let referrals = [];
    let stats = {
      referrals: 0,
      applies: 0,
      hires: 0,
      completions: 0
    };
    
    // Handle the API response structure
    if (response && response.success && response.data) {
      const data = response.data;
      console.log('[ApiService] Processing API response data:', data);
      
      // Extract stats from overallStatus
      if (data.overallStatus) {
        stats = {
          referrals: data.overallStatus.referrals || 0,
          applies: data.overallStatus.applies || 0,
          hires: data.overallStatus.hires || 0,
          completions: data.overallStatus.completions || 0
        };
        console.log('[ApiService] Extracted stats from overallStatus:', stats);
      }
      
      // Extract referrals from candidateDetails
      if (data.candidateDetails && Array.isArray(data.candidateDetails)) {
        referrals = data.candidateDetails.map((item, index) => ({
          id: item.id || `referral-${index}`,
          name: item.name || 'No Name',
          mobile: item.mobile_number || item.mobile || '',
          email: item.email || '',
          jobId: item.jobId || null,
          jobTitle: item.jobTitle || 'Direct Referral',
          status: item.status || 'PENDING',
          referredBy: item.referredBy || '',
          referredDate: item.referredDate || new Date().toISOString().split('T')[0],
          reward: item.reward || 0,
          applicationCount: item.appliedCount || 0
        }));
        console.log('[ApiService] Processed candidateDetails:', referrals);
      }
    } else {
      console.log('[ApiService] Unexpected response format, trying direct access');
      
      // Try direct access to response properties
      if (response && response.overallStatus) {
        stats = {
          referrals: response.overallStatus.referrals || 0,
          applies: response.overallStatus.applies || 0,
          hires: response.overallStatus.hires || 0,
          completions: response.overallStatus.completions || 0
        };
        console.log('[ApiService] Direct stats extraction:', stats);
      }
      
      if (response && response.candidateDetails && Array.isArray(response.candidateDetails)) {
        referrals = response.candidateDetails.map((item, index) => ({
          id: item.id || `referral-${index}`,
          name: item.name || 'No Name',
          mobile: item.mobile_number || item.mobile || '',
          email: item.email || '',
          jobId: item.jobId || null,
          jobTitle: item.jobTitle || 'Direct Referral',
          status: item.status || 'PENDING',
          referredBy: item.referredBy || '',
          referredDate: item.referredDate || new Date().toISOString().split('T')[0],
          reward: item.reward || 0,
          applicationCount: item.appliedCount || 0
        }));
        console.log('[ApiService] Direct candidateDetails processing:', referrals);
      }
    }
    
    // Calculate stats if not provided (Flutter format)
    if (stats.referrals === 0 && referrals.length > 0) {
      stats = {
        referrals: referrals.length,
        applies: referrals.reduce((sum, r) => sum + r.applicationCount, 0),
        hires: referrals.filter(r => r.status === 'HIRED').length,
        completions: 0 // This would need to be calculated based on completion status
      };
    }
    
    console.log(`[ApiService] Processed ${referrals.length} referrals with stats:`, stats);
    
    return {
      referrals,
      stats
    };
    
  } catch (error) {
    console.error('[ApiService] Error fetching referrals:', error);
    // Return empty data instead of throwing to prevent UI errors
    return {
      referrals: [],
      stats: {
        referrals: 0,
        applies: 0,
        hires: 0,
        completions: 0
      }
    };
  }
};

/**
 * Get referral statistics
 * @returns {Promise} - Referral statistics
 */
export const getReferralStats = async () => {
  // This will be part of getReferrals response
  const data = await getReferrals();
  return data.stats || {
    referrals: 0,
    applies: 0,
    hires: 0,
    completions: 0
  };
};

/**
 * Create a referral (matching Flutter implementation)
 * @param {object} referralData - Referral data with jobId and candidates
 * @returns {Promise} - Referral creation result
 */
export const createReferral = async (referralData) => {
  try {
    console.log('[ApiService] createReferral called with:', referralData);
    
    const mobileNumber = getMobileNumber();
    console.log('[ApiService] Mobile number from storage:', mobileNumber);
    
    const { jobId, candidates } = referralData;
    console.log('[ApiService] Extracted jobId:', jobId);
    console.log('[ApiService] Extracted candidates:', candidates);
  
  // For direct referrals, we don't need a job ID
  const isDirectReferral = !jobId || (typeof jobId === 'string' && jobId.trim() === '');
  
  if (!isDirectReferral && (typeof jobId === 'string' && jobId.trim() === '')) {
    console.error('[ApiService] ERROR: Cannot create referral with empty job ID');
    return {
      success: false,
      message: 'Cannot create referral: Job ID is empty'
    };
  }
  
  // Create URL with query parameters
  const url = new URL(API_ENDPOINTS.createReferral);
  url.searchParams.append('mobileNumber', mobileNumber);
  
  // Always add positionId - use 0 for direct referrals, job ID for job-specific referrals
  if (isDirectReferral) {
    url.searchParams.append('positionId', '0'); // Use 0 to indicate direct referral
  } else {
    const positionId = typeof jobId === 'string' ? jobId.trim() : String(jobId);
    url.searchParams.append('positionId', positionId);
  }
  
  // Build payload with both mobile number and candidate name as per new API contract
  const candidatePayload = candidates.map(candidate => ({
    mobileNumber: candidate.mobile || candidate.phone || '',
    candidateName: candidate.name || candidate.candidateName || ''
  }));
  
  console.log(`[ApiService] Creating ${isDirectReferral ? 'direct' : 'job-specific'} referral`);
  console.log(`[ApiService] Position ID: ${isDirectReferral ? '0' : jobId}`);
  console.log(`[ApiService] Referral URL with params: ${url.toString()}`);
  console.log(`[ApiService] Referral payload (objects):`, candidatePayload);
  
  try {
    const response = await apiRequest(url.toString(), {
      method: 'POST',
      body: JSON.stringify(candidatePayload), // Send array of { mobileNumber, candidateName }
    });
    
    console.log('[ApiService] Create referral response (wrapped):', response);
    
    // Expect apiRequest to return a wrapper: { success, data, status, error }
    if (!response || response.success === false) {
      const message = response?.error || 'Failed to add candidate';
      return {
        success: false,
        message,
      };
    }
    
    const data = response.data || {};
    const message = data.response || data.message || 'Referrals added successfully';
    
    // Check if position is unavailable (inactive, filled, etc.)
    if (data.positionUnavailable === true) {
      // Return user-friendly error message for position unavailability
      let errorMessage = message;
      if (message.includes('position is inactive') || message.includes('position is closed')) {
        errorMessage = 'This position is no longer accepting applications.';
      } else if (message.includes('no vacant positions')) {
        errorMessage = 'Sorry, all positions have been filled for this job.';
      } else if (message.includes('Cannot add candidate')) {
        errorMessage = 'This position is currently not available for applications.';
      }
      return {
        success: false,
        message: errorMessage,
        positionUnavailable: true,
        data: { raw: data }
      };
    }
    
    return {
      success: true,
      message,
      data: {
        newCandidates: Array.isArray(data.newCandidates) ? data.newCandidates : [],
        existingCandidates: Array.isArray(data.existingCandidates) ? data.existingCandidates : [],
        raw: data,
      }
    };
    
  } catch (error) {
    console.error('[ApiService] Create referral exception:', error);
    console.error('[ApiService] Exception details:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
    return {
      success: false,
      message: `Error creating referral: ${error.message}`
    };
  }
  } catch (outerError) {
    console.error('[ApiService] Outer exception in createReferral:', outerError);
    return {
      success: false,
      message: `Unexpected error: ${outerError.message}`
    };
  }
};

/**
 * Update referral status (matching Flutter implementation)
 * @param {string} referralId - Referral ID
 * @param {string} status - New status
 * @returns {Promise} - Update result
 */
export const updateReferralStatus = async (referralId, status) => {
  const mobileNumber = getMobileNumber();
  
  const requestData = {
    mobileNumber,
    referralId,
    status
  };

  console.log(`[ApiService] Updating referral status: ${referralId} to: ${status}`);
  
  try {
    const response = await apiRequest(API_ENDPOINTS.updateReferralStatus, {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
    
    console.log('[ApiService] Update referral status response:', response);
    
    return {
      success: true,
      message: response?.message || 'Status updated successfully',
      data: response
    };
    
  } catch (error) {
    console.error('[ApiService] Update referral status error:', error);
    return {
      success: false,
      message: `Error updating referral status: ${error.message}`
    };
  }
};

/**
 * Get user earnings
 * @returns {Promise} - Earnings data
 */
export const getEarnings = async () => {
  const mobileNumber = getMobileNumber();
  
  const url = new URL(API_ENDPOINTS.fetchEarnings);
  url.searchParams.append('mobileNumber', mobileNumber);

  console.log(`[ApiService] Fetching earnings for mobile: ${mobileNumber}`);
  
  return apiRequest(url.toString(), {
    method: 'GET',
  });
};

/**
 * Get candidate profile by mobile number (using getUserProfile API with programmatic token)
 * @returns {Promise} - Candidate profile data
 */
export const getCandidateByMobile = async () => {
  const mobileNumber = getMobileNumber();
  
  if (!mobileNumber || mobileNumber === '0') {
    console.log('[ApiService] No valid mobile number available');
    return {
      success: false,
      error: 'No mobile number available',
      status: 400
    };
  }

  // Use getCandidateByMobile API endpoint
  const candidateUrl = `${API_ENDPOINTS.getCandidateByMobile}${mobileNumber}`;
  
  console.log(`[ApiService] Fetching candidate profile for mobile: ${mobileNumber}`);
  console.log(`[ApiService] Candidate URL: ${candidateUrl}`);
  
  try {
    // Step 1: Get programmatic token (cached or fetch new)
    console.log('[ApiService] Step 1: Getting programmatic token...');
    
    const programmaticToken = await getProgrammaticToken();
    
    if (!programmaticToken) {
      console.log('[ApiService] ❌ Failed to get programmatic token');
      return {
        success: false,
        error: 'Failed to authenticate - could not get token',
        status: 401
      };
    }
    
    console.log(`[ApiService] ✅ Got programmatic token: ${programmaticToken.substring(0, 10)}...`);
    console.log('[ApiService] Token length:', programmaticToken.length);
    
    // Step 2: Use token to call getCandidateByMobile API (candidate details API)
    console.log('[ApiService] Step 2: Calling getCandidateByMobile API with token...');
    console.log('[ApiService] About to call candidate API with URL:', candidateUrl);
    console.log('[ApiService] Using Authorization header:', `Bearer ${programmaticToken?.substring(0, 10) || 'null'}...`);
    
    const response = await apiRequest(candidateUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${programmaticToken}`,
        'Content-Type': 'application/json',
      },
    });
    
    console.log('[ApiService] ✅ Candidate API call completed');
    
    console.log(`[ApiService] Candidate profile response status: ${response.status}`);
    
    if (response.success) {
      console.log('[ApiService] Candidate profile data:', response.data);

      const candidateIdValue = response.data?.candidateId || response.data?.id;
      if (candidateIdValue !== undefined && candidateIdValue !== null && candidateIdValue !== '') {
        try {
          localStorage.setItem('candidateId', String(candidateIdValue));
          console.log('[ApiService] Stored candidateId from profile:', candidateIdValue);
        } catch (storageError) {
          console.warn('[ApiService] Unable to store candidateId in localStorage:', storageError);
        }
      }

      return {
        success: true,
        data: {
          fullName: response.data?.fullName || '',
          email: response.data?.emailId || response.data?.email || '',
          mobile: response.data?.mobile || mobileNumber,
          resumeFileName: response.data?.resumeFileName || 'Resume.pdf',
          ...response.data
        },
        status: response.status
      };
    } else if (response.status === 404) {
      // Candidate not found - not an error, just return null data
      console.log(`[ApiService] Candidate not found (404) for mobile: ${mobileNumber}`);
      return {
        success: true,
        data: null,
        status: 404
      };
    } else {
      console.log('[ApiService] Error response:', response.error);
      return {
        success: false,
        error: response.error || 'Failed to get candidate profile',
        status: response.status
      };
    }
    
  } catch (error) {
    console.error('[ApiService] Error getting candidate profile:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      status: 500
    };
  }
};

/**
 * Get user profile (legacy function - now uses getCandidateByMobile)
 * @returns {Promise} - User profile data
 */
export const getUserProfile = async () => {
  console.log('[ApiService] getUserProfile called - redirecting to getCandidateByMobile');
  return getCandidateByMobile();
};

/**
 * Get budget details by ID (legacy microservice) with Authorization
 * @param {string} budgetId - Budget ID
 * @returns {Promise<{success:boolean, data?:any, status?:number, error?:string}>}
 */
export const getBudgetDetailsById = async (budgetId) => {
  try {
    if (!budgetId) {
      return { success: false, error: 'budgetId is required', status: 400 };
    }

    // Acquire programmatic token
    let token = await getProgrammaticToken();
    if (!token) {
      return { success: false, error: 'Authentication failed', status: 401 };
    }

    const url = new URL(API_ENDPOINTS.fetchBudgetDetailsById);
    url.searchParams.append('budgetId', String(budgetId));

    console.log('[ApiService] Fetching budget details by ID:', budgetId);
    console.log('[ApiService] Budget details URL:', url.toString());

    // Do the call, retry once on 401 by refreshing token
    const doCall = async () => apiRequest(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    let resp = await doCall();
    if (resp?.status === 401) {
      console.warn('[ApiService] Budget details 401, refreshing programmatic token and retrying...');
      try {
        // Clear cached token and timestamp
        localStorage.removeItem('programmaticToken');
        localStorage.removeItem('programmaticTokenTimestamp');
      } catch (_) {}
      token = await getProgrammaticToken();
      if (token) {
        resp = await apiRequest(url.toString(), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        });
      }
    }

    return resp;
  } catch (error) {
    console.error('[ApiService] Error fetching budget details:', error);
    return { success: false, error: error.message || 'Network error', status: 500 };
  }
};

/**
 * Update user profile using PUT method with candidate ID (matching Flutter updateCandidate)
 * @param {object} profileData - Profile data to update
 * @returns {Promise} - Update result
 */
export const updateUserProfile = async (profileData) => {
  try {
    const mobileNumber = getMobileNumber();
    let candidateId = getCandidateId();
    
    if (!mobileNumber || mobileNumber === '0') {
      return {
        success: false,
        error: 'Mobile number is required',
        status: 400
      };
    }

    if (!candidateId) {
      console.warn('[ApiService] No candidate ID found in storage. Attempting to fetch from candidate profile API.');
      try {
        const candidateLookup = await getCandidateByMobile();
        if (candidateLookup?.success && candidateLookup?.data) {
          const derivedCandidateId = candidateLookup.data.candidateId || candidateLookup.data.id;
          if (derivedCandidateId !== undefined && derivedCandidateId !== null && derivedCandidateId !== '') {
            candidateId = String(derivedCandidateId);
            try {
              localStorage.setItem('candidateId', candidateId);
            } catch (storageError) {
              console.warn('[ApiService] Unable to store fetched candidateId in localStorage:', storageError);
            }
            console.log('[ApiService] Retrieved candidateId from profile API:', candidateId);
          }
        } else {
          console.warn('[ApiService] Unable to retrieve candidateId from profile API:', candidateLookup?.error || 'Unknown error');
        }
      } catch (lookupError) {
        console.error('[ApiService] Error fetching candidateId from profile API:', lookupError);
      }
    }

    if (!candidateId) {
      return {
        success: false,
        error: 'Candidate ID is required',
        status: 400
      };
    }

    console.log(`[ApiService] Updating profile for mobile: ${mobileNumber}`);
    console.log(`[ApiService] Using candidate ID: ${candidateId}`);
    console.log('[ApiService] Profile data to update:', profileData);

    // Get programmatic token (cached or fetch new)
    const token = await getProgrammaticToken();

    if (!token) {
      console.error('[ApiService] Failed to get authentication token');
      return {
        success: false,
        error: 'Authentication failed',
        status: 401
      };
    }

    // Parse names from fullName (matching Flutter implementation)
    const nameParts = profileData.fullName ? profileData.fullName.trim().split(' ') : [];
    const firstName = nameParts.length > 0 ? nameParts[0] : '';
    const middleName = nameParts.length > 2 ? nameParts[1] : '';
    let lastName = '';
    
    if (nameParts.length === 2) {
      lastName = nameParts[1];
    } else if (nameParts.length > 2) {
      lastName = nameParts.slice(2).join(' ');
    }

    // Use existing city data from profile or leave empty for backend to handle
    const currentCity = profileData.currentCity || profileData.city || '';

    // Create JSON payload with ALL form field values (using empty strings instead of null)
    const candidateData = {
      candidateId: parseInt(candidateId, 10),
      firstName: firstName || '',
      middleName: middleName || '',
      lastName: lastName || '',
      fullName: profileData.fullName || '',
      mobileNumber: mobileNumber || '',
      emailId: profileData.email || '',
      cityName: currentCity || '',
      currentCity: currentCity || '',
      preferredCity: currentCity || '',
      expectedSalary: profileData.expectedSalary ? parseFloat(profileData.expectedSalary) : 0,
      pincode: profileData.pincode && String(profileData.pincode).trim() !== '' ? parseInt(profileData.pincode) : null,
      // Additional fields to ensure complete data
      email: profileData.email || '', // Fallback email field
      salary: profileData.expectedSalary ? parseFloat(profileData.expectedSalary) : 0, // Alternative salary field
      location: currentCity || '', // Location field
      city: currentCity || '' // City field
    };

    // Debug: Print JSON payload being sent
    console.log('[ApiService] --- DEBUG JSON PAYLOAD ---');
    console.log(JSON.stringify(candidateData, null, 2));
    console.log('[ApiService] --- END DEBUG JSON PAYLOAD ---');
    
    // Construct the endpoint URL with candidate ID
    const updateEndpoint = `${API_ENDPOINTS.updateUserProfile}${candidateId}`;
    console.log('[ApiService] Sending PUT request to:', updateEndpoint);
    console.log('[ApiService] Using token:', token?.substring(0, 10) + '...');

    // Make the API request with JSON data (matching Flutter implementation)
    const response = await fetch(updateEndpoint, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(candidateData)
    });

    console.log('[ApiService] Profile update response status:', response.status);

    let responseData = null;
    const responseText = await response.text();
    console.log('[ApiService] Profile update response text:', responseText);

    // Try to parse JSON response
    if (responseText && responseText.trim()) {
      try {
        responseData = JSON.parse(responseText);
        console.log('[ApiService] Profile update parsed response:', responseData);
      } catch (parseError) {
        console.warn('[ApiService] Failed to parse response as JSON:', parseError);
        responseData = { message: responseText };
      }
    }

    if (response.ok) {
      return {
        success: true,
        data: responseData,
        status: response.status
      };
    } else {
      return {
        success: false,
        error: responseData?.message || responseText || `HTTP ${response.status}`,
        status: response.status
      };
    }

  } catch (error) {
    console.error('[ApiService] Error updating profile:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      status: 500
    };
  }
};

/**
 * Fetch candidate skills (active)
 * @returns {Promise} - Candidate skills data
 */
export const fetchCandidateSkills = async () => {
  const mobileNumber = getMobileNumber();
  
  if (!mobileNumber) {
    console.log('[ApiService] ❌ No mobile number found for fetching skills');
    return {
      success: false,
      error: 'Mobile number not found. Please sign in again.',
      status: 401
    };
  }

  try {
    console.log(`[ApiService] Fetching candidate skills for mobile: ${mobileNumber}`);
    
    const response = await apiRequest(`${API_ENDPOINTS.fetchCandidateSkills}?mobileNumber=${mobileNumber}`, {
      method: 'GET'
    });
    
    console.log('[ApiService] Skills API response:', response);
    
    if (response.success) {
      console.log('[ApiService] ✅ Successfully fetched candidate skills');
      return {
        success: true,
        data: response.data || [],
        status: response.status
      };
    } else {
      console.log('[ApiService] ❌ Failed to fetch skills:', response.error);
      return {
        success: false,
        error: response.error || 'Failed to fetch skills',
        status: response.status
      };
    }
    
  } catch (error) {
    console.error('[ApiService] Error fetching candidate skills:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      status: 500
    };
  }
};

/**
 * Fetch inactive candidate skills (previously used skills) - WITH fallback to top 5 common skills
 * @returns {Promise} - Inactive candidate skills data with skillId and skillName
 */
export const fetchInactiveCandidateSkills = async () => {
  const mobileNumber = getMobileNumber();
  
  if (!mobileNumber) {
    console.log('[ApiService] ❌ No mobile number found for fetching inactive skills');
    return {
      success: false,
      error: 'Mobile number not found. Please sign in again.',
      status: 401
    };
  }

  try {
    console.log(`[ApiService] Fetching inactive candidate skills for mobile: ${mobileNumber}`);
    
    const response = await apiRequest(`${API_ENDPOINTS.fetchInactiveCandidateSkills}?mobileNumber=${mobileNumber}`, {
      method: 'GET'
    });
    
    console.log('[ApiService] Inactive skills API response:', response);
    
    if (response.success) {
      console.log('[ApiService] ✅ Successfully fetched inactive candidate skills:', response.data);
      return {
        success: true,
        data: response.data || [],
        status: response.status
      };
    } else {
      console.log('[ApiService] ❌ Failed to fetch inactive skills:', response.error);
      return {
        success: false,
        error: response.error || 'Failed to fetch inactive skills',
        status: response.status
      };
    }
    
  } catch (error) {
    console.error('[ApiService] Error fetching inactive candidate skills:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      status: 500
    };
  }
};

/**
 * Fetch inactive candidate skills WITHOUT fallback - for skill matching/highlighting only
 * @returns {Promise} - Inactive candidate skills data with skillId and skillName (no fallback)
 */
export const fetchInactiveCandidateSkillsWithoutFallback = async () => {
  const mobileNumber = getMobileNumber();
  
  if (!mobileNumber) {
    console.log('[ApiService] ❌ No mobile number found for fetching inactive skills without fallback');
    return {
      success: false,
      error: 'Mobile number not found. Please sign in again.',
      status: 401
    };
  }

  try {
    console.log(`[ApiService] Fetching inactive candidate skills WITHOUT fallback for mobile: ${mobileNumber} (for skill highlighting)`);
    
    const response = await apiRequest(`${API_ENDPOINTS.fetchInactiveCandidateSkillsWithoutFallback}?mobileNumber=${mobileNumber}`, {
      method: 'GET'
    });
    
    console.log('[ApiService] Inactive skills (without fallback) API response:', response);
    
    if (response.success) {
      console.log('[ApiService] ✅ Successfully fetched actual inactive candidate skills (no fallback):', response.data);
      return {
        success: true,
        data: response.data || [],
        status: response.status
      };
    } else {
      console.log('[ApiService] ❌ Failed to fetch inactive skills without fallback:', response.error);
      return {
        success: false,
        error: response.error || 'Failed to fetch inactive skills',
        status: response.status
      };
    }
    
  } catch (error) {
    console.error('[ApiService] Error fetching inactive candidate skills without fallback:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      status: 500
    };
  }
};

/**
 * Save candidate skills
 * @param {Array} skills - Array of skill names or skill objects to save
 * @param {Array} allActiveSkills - Optional array of all active skills for ID mapping (to avoid API call)
 * @returns {Promise} - Save result
 */
export const saveCandidateSkills = async (skills, allActiveSkills = null) => {
  const mobileNumber = getMobileNumber();
  
  if (!mobileNumber) {
    console.log('[ApiService] ❌ No mobile number found for saving skills');
    return {
      success: false,
      error: 'Mobile number not found. Please sign in again.',
      status: 401
    };
  }

  try {
    console.log(`[ApiService] Saving candidate skills for mobile: ${mobileNumber}`);
    console.log('[ApiService] Skills to save:', skills);
    
    // Use provided skills or fetch if not available
    let skillsData = allActiveSkills;
    if (!skillsData || skillsData.length === 0) {
      console.log('[ApiService] ⚠️ No skills mapping provided, fetching from API');
      const allSkillsResponse = await fetchAllActiveSkills();
      if (!allSkillsResponse.success) {
        console.log('[ApiService] ❌ Failed to fetch skills for ID mapping');
        return {
          success: false,
          error: 'Failed to fetch skills for mapping',
          status: 500
        };
      }
      skillsData = allSkillsResponse.data;
    } else {
      console.log('[ApiService] ✅ Using provided skills mapping, no API call made');
    }
    
    // Create skill name to ID mapping
    const skillNameToId = {};
    skillsData.forEach(skill => {
      const skillName = typeof skill === 'string' ? skill : (skill.name || skill.skillName || skill);
      const skillId = typeof skill === 'string' ? null : (skill.id || skill.skillId);
      if (skillName && skillId) {
        skillNameToId[skillName] = skillId;
      }
    });
    
    console.log('[ApiService] Skill name to ID mapping:', skillNameToId);
    
    // Map selected skill names to IDs
    const skillIds = skills.map(skill => {
      const skillName = typeof skill === 'string' ? skill : (skill.name || skill.skillName || skill);
      return skillNameToId[skillName];
    }).filter(id => id !== undefined && id !== null);
    
    console.log('[ApiService] Mapped skill IDs:', skillIds);
    
    if (skillIds.length === 0) {
      console.log('[ApiService] ❌ No valid skill IDs found');
      return {
        success: false,
        error: 'No valid skills to save',
        status: 400
      };
    }
    
    // Get programmatic token for authentication
    const tokenResponse = await apiRequest(API_ENDPOINTS.userToken, {
      method: 'GET'
    });
    
    if (!tokenResponse.success) {
      console.log('[ApiService] ❌ Failed to get authentication token');
      return {
        success: false,
        error: 'Failed to authenticate',
        status: 401
      };
    }
    
    const programmaticToken = tokenResponse.data?.token || tokenResponse.data;
    console.log('[ApiService] Got programmatic token for skills save');
    
    // Create query parameters as expected by the API
    const skillIdsParam = skillIds.join(',');
    const url = `${API_ENDPOINTS.saveCandidateSkills}?skillIds=${skillIdsParam}&mobileNumber=${mobileNumber}`;
    
    console.log('[ApiService] Save skills URL:', url);
    
    // This endpoint returns plain text instead of JSON, so we need custom handling
    const fetchResponse = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${programmaticToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    console.log('[ApiService] Save skills API response status:', fetchResponse.status);
    
    if (fetchResponse.ok) {
      // API returns plain text "Skills saved successfully"
      const textResponse = await fetchResponse.text();
      console.log('[ApiService] ✅ Successfully saved candidate skills:', textResponse);
      return {
        success: true,
        data: { message: textResponse },
        status: fetchResponse.status
      };
    } else {
      const errorText = await fetchResponse.text();
      console.log('[ApiService] ❌ Failed to save skills:', errorText);
      return {
        success: false,
        error: errorText || 'Failed to save skills',
        status: fetchResponse.status
      };
    }
    
  } catch (error) {
    console.error('[ApiService] Error saving candidate skills:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      status: 500
    };
  }
};

/**
 * Mark candidate skills as removed (for system-added skills)
 * @param {Array<number>} skillIds - Array of skill IDs to mark as removed
 * @returns {Promise} - API response
 */
export const markCandidateSkillsAsRemoved = async (skillIds) => {
  const mobileNumber = getMobileNumber();
  
  if (!mobileNumber || mobileNumber === '0') {
    console.log('[ApiService] ❌ No mobile number found for marking skills as removed');
    return {
      success: false,
      error: 'Mobile number not found. Please sign in again.',
      status: 401
    };
  }

  if (!skillIds || skillIds.length === 0) {
    console.log('[ApiService] ⚠️ No skill IDs provided to mark as removed');
    return {
      success: true,
      data: 'No skills to remove',
      status: 200
    };
  }

  try {
    console.log(`[ApiService] Marking skills as removed for mobile: ${mobileNumber}`);
    console.log('[ApiService] Skill IDs to mark as removed:', skillIds);
    
    // Create FormData for the request
    const formData = new FormData();
    formData.append('mobileNumber', mobileNumber);
    formData.append('skillIds', skillIds.join(','));
    
    console.log('[ApiService] Request URL:', API_ENDPOINTS.markCandidateSkillsAsRemoved);
    console.log('[ApiService] Request params:', { mobileNumber, skillIds: skillIds.join(',') });
    
    const response = await fetch(API_ENDPOINTS.markCandidateSkillsAsRemoved, {
      method: 'POST',
      body: formData
    });
    
    console.log('[ApiService] Mark skills as removed response status:', response.status);
    
    if (response.ok) {
      const result = await response.text();
      console.log('[ApiService] ✅ Successfully marked skills as removed:', result);
      return {
        success: true,
        data: result,
        status: response.status
      };
    } else {
      const errorText = await response.text();
      console.log('[ApiService] ❌ Failed to mark skills as removed:', errorText);
      return {
        success: false,
        error: errorText || 'Failed to mark skills as removed',
        status: response.status
      };
    }
    
  } catch (error) {
    console.error('[ApiService] Error marking skills as removed:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      status: 500
    };
  }
};

/**
 * Fetch all active skills (for skill selection)
 * @returns {Promise} - All available skills
 */
export const fetchAllActiveSkills = async () => {
  try {
    console.log('[ApiService] Fetching all active skills...');
    
    const response = await apiRequest(API_ENDPOINTS.fetchAllActiveSkills, {
      method: 'GET'
    });
    
    console.log('[ApiService] All skills API response:', response);
    
    if (response.success) {
      // Normalize payload into a flat, deduplicated array for UI dropdowns
      const normalize = (raw) => {
        // 1) Find the list container: support multiple shapes
        let list = [];
        if (Array.isArray(raw)) {
          list = raw;
        } else if (raw && typeof raw === 'object') {
          const candidates = [raw.skills, raw.data, raw.list, raw.items, raw.result];
          for (const c of candidates) {
            if (Array.isArray(c) && c.length) { list = c; break; }
          }
        }

        // 2) Flatten nested arrays
        list = list.flat ? list.flat() : [].concat(...list);

        // 3) Map to uniform label-bearing structures and filter actives
        const toLabel = (item) => {
          if (!item) return '';
          if (typeof item === 'string') return item.trim();
          if (typeof item === 'object') {
            const label = (
              item.skillName || item.name || item.label || item.skill || item.title || item.skill_name || ''
            );
            return String(label).trim();
          }
          return '';
        };

        const isActive = (item) => {
          if (!item || typeof item !== 'object') return true; // assume active if not specified
          // Common flags seen across APIs
          if (typeof item.isActive !== 'undefined') return Boolean(item.isActive);
          if (typeof item.active !== 'undefined') return Boolean(item.active);
          return true;
        };

        const mapped = [];
        for (const it of list) {
          const label = toLabel(it);
          if (!label) continue;
          if (typeof it === 'object' && !isActive(it)) continue;
          // Keep object with stable fields to leverage keys if needed
          if (typeof it === 'object') {
            mapped.push({
              id: it.id || it.skillId || it.value || label,
              skillName: label,
            });
          } else {
            mapped.push(label);
          }
        }

        // 4) De-duplicate by label (case-insensitive)
        const seen = new Set();
        const deduped = [];
        for (const item of mapped) {
          const label = typeof item === 'string' ? item : (item.skillName || item.name || '');
          const key = label.toLowerCase();
          if (!label || seen.has(key)) continue;
          seen.add(key);
          deduped.push(item);
        }

        return deduped;
      };

      const normalized = normalize(response.data);
      console.log(`[ApiService] ✅ Active skills normalized: ${normalized.length} items`);
      return {
        success: true,
        data: normalized,
        status: response.status
      };
    } else {
      console.log('[ApiService] ❌ Failed to fetch all skills:', response.error);
      return {
        success: false,
        error: response.error || 'Failed to fetch all skills',
        status: response.status
      };
    }
    
  } catch (error) {
    console.error('[ApiService] Error fetching all active skills:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      status: 500
    };
  }
};

/**
 * Fetch notifications for the last 7 days
 * @param {{candidateId?: string|number, notificationType?: string}} params
 */
export const fetchNotifications = async (params = {}) => {
  const { candidateId, notificationType } = params || {};
  const mobileNumber = getMobileNumber();

  const url = new URL(API_ENDPOINTS.fetchNotifications);
  if (mobileNumber && mobileNumber !== '0') url.searchParams.append('mobileNumber', mobileNumber);
  if (candidateId) url.searchParams.append('candidateId', String(candidateId));
  if (notificationType) url.searchParams.append('notificationType', notificationType);

  return apiRequest(url.toString(), { method: 'GET' });
};

/**
 * Mark a notification as read
 * @param {string|number} notificationId
 */
export const markNotificationAsRead = async (notificationId) => {
  const id = Number(notificationId);
  if (!id || Number.isNaN(id)) {
    return { success: false, error: 'Invalid notificationId', status: 400 };
  }

  const mobileNumber = getMobileNumber();
  const url = new URL(API_ENDPOINTS.markNotificationAsRead);
  url.searchParams.append('notificationId', id.toString());
  if (mobileNumber && mobileNumber !== '0') {
    url.searchParams.append('mobileNumber', mobileNumber);
  }

  // Backend expects request parameters (Spring @RequestParam), not JSON body
  return apiRequest(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Accept': 'application/json',
    },
  });
};

/**
 * Set engagement flag for notifications (backend requires candidateId as @RequestParam)
 * @param {number|string} [candidateId]
 */
export const setNotificationEngagementFlag = async (candidateId) => {
  const url = new URL(API_ENDPOINTS.setNotificationEngagementFlag);
  const mobileNumber = getMobileNumber();

  // Try to resolve candidateId if not provided
  let cid = candidateId;
  if (!cid) {
    try {
      cid = getCandidateId && getCandidateId();
    } catch (_) {
      // ignore
    }
  }

  if (!cid) {
    return { success: false, error: 'candidateId is required', status: 400 };
  }

  url.searchParams.append('candidateId', String(cid));
  if (mobileNumber && mobileNumber !== '0') {
    url.searchParams.append('mobileNumber', mobileNumber);
  }

  return apiRequest(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Accept': 'application/json',
    },
  });
};

/**
 * Get total unread notification count
 * @param {string|number} candidateId
 */
export const getJobDetails = async (jobId) => {
    // This is likely the same as getJobById, but creating a specific function for clarity
    const url = new URL(`${API_ENDPOINTS.getJobById.replace(':id', jobId)}`);
    return apiRequest(url.toString(), { method: 'GET' });
};

export const getApplicationStatus = async (candidateId, jobId) => {
    const url = new URL(API_ENDPOINTS.getApplicationStatus.replace(':candidateId', candidateId).replace(':jobId', jobId));
    return apiRequest(url.toString(), { method: 'GET' });
};

export const getTotalUnreadNotificationCount = async (candidateId) => {
  const mobileNumber = getMobileNumber();
  const url = new URL(API_ENDPOINTS.getTotalUnreadNotificationCount);
  if (mobileNumber && mobileNumber !== '0') url.searchParams.append('mobileNumber', mobileNumber);
  if (candidateId) url.searchParams.append('candidateId', String(candidateId));
  return apiRequest(url.toString(), { method: 'GET' });
};

/**
 * Fetch KYC details for a candidate
 * @returns {Promise} - KYC details
 */
export const fetchCandidateKYCDetails = async () => {
  try {
    const mobileNumber = getMobileNumber();
    if (!mobileNumber || mobileNumber === '0') {
      return {
        success: false,
        error: 'Mobile number is required',
        status: 400
      };
    }

    console.log('[ApiService] Fetching KYC details for mobile:', mobileNumber);
    
    // Get programmatic token (cached or fetch new)
    const token = await getProgrammaticToken();

    if (!token) {
      console.error('[ApiService] Failed to get authentication token');
      return {
        success: false,
        error: 'Authentication failed',
        status: 401
      };
    }
    if (!token) {
      return {
        success: false,
        error: 'No authentication token received',
        status: 401
      };
    }

    const url = new URL(API_ENDPOINTS.fetchCandidateKYCDetails);
    url.searchParams.append('mobileNumber', mobileNumber);

    const response = await apiRequest(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    console.log('[ApiService] KYC details response:', response);

    if (response.success) {
      // Process the KYC data similar to Flutter implementation
      const data = response.data || {};
      
      // Check for minimal response case
      const isMinimalResponse = Object.keys(data).length <= 1 && data.hasOwnProperty('nameOnAadhaar');
      
      let kycData;
      if (isMinimalResponse) {
        // Minimal response - create default structure
        kycData = {
          aadhaarNumber: '',
          aadhaarVerified: false,
          nameOnAadhaar: data.nameOnAadhaar || null,
          panNumber: '',
          panVerified: false,
          // accept either key casing from backend
          nameOnPan: data.nameOnPan || data.nameOnPAN || null,
          phoneNumber: mobileNumber
        };
      } else {
        // Normal response with verification data
        kycData = {
          aadhaarNumber: data.aadhaarNumber || '',
          aadhaarVerified: data.aadhaarVerified || (data.aadhaarNumber && data.aadhaarNumber.length === 12),
          nameOnAadhaar: data.nameOnAadhaar || null,
          panNumber: data.panNumber || '',
          panVerified: data.panVerified || (data.panNumber && data.panNumber.length === 10),
          // accept either key casing from backend
          nameOnPan: data.nameOnPan || data.nameOnPAN || null,
          phoneNumber: mobileNumber
        };
      }

      return {
        success: true,
        data: kycData,
        status: response.status
      };
    }

    return response;
  } catch (error) {
    console.error('[ApiService] Error fetching KYC details:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      status: 500
    };
  }
};

/**
 * Request Aadhaar OTP
 * @param {string} aadhaarNumber - 12-digit Aadhaar number
 * @returns {Promise} - OTP request result
 */
export const requestAadhaarOtp = async (aadhaarNumber) => {
  try {
    const mobileNumber = getMobileNumber();
    if (!mobileNumber || mobileNumber === '0') {
      return {
        success: false,
        error: 'Mobile number is required',
        status: 400
      };
    }

    console.log('[ApiService] Requesting Aadhaar OTP for:', aadhaarNumber);

    // Get programmatic token
    const tokenUrl = new URL(API_ENDPOINTS.userToken);
    tokenUrl.searchParams.append('mobileNumber', mobileNumber);
    
    const tokenResponse = await apiRequest(tokenUrl.toString(), {
      method: 'GET'
    });

    if (!tokenResponse.success) {
      return {
        success: false,
        error: 'Authentication failed',
        status: 401
      };
    }

    const token = tokenResponse.data?.token;
    const url = `${API_ENDPOINTS.sendAadhaarOTP}${aadhaarNumber}`;

    const response = await apiRequest(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });

    console.log('[ApiService] Aadhaar OTP response:', response);
    return response;
  } catch (error) {
    console.error('[ApiService] Error requesting Aadhaar OTP:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      status: 500
    };
  }
};

/**
 * Verify Aadhaar OTP
 * @param {string} otp - 6-digit OTP
 * @param {string} aadhaarNumber - 12-digit Aadhaar number
 * @param {string} refId - Reference ID
 * @param {string} mobileNumber - Mobile number
 * @returns {Promise} - Verification result
 */
export const verifyAadhaarOtp = async (otp, aadhaarNumber, refId, mobileNumber) => {
  try {
    const mobile = mobileNumber || getMobileNumber();
    if (!mobile || mobile === '0') {
      return {
        success: false,
        error: 'Mobile number is required',
        status: 400
      };
    }

    if (!refId) {
      return {
        success: false,
        error: 'Reference ID is required for Aadhaar verification',
        status: 400
      };
    }

    console.log('[ApiService] Verifying Aadhaar OTP with refId:', refId);

    // Get programmatic token
    const tokenUrl = new URL(API_ENDPOINTS.userToken);
    tokenUrl.searchParams.append('mobileNumber', mobile);
    
    const tokenResponse = await apiRequest(tokenUrl.toString(), {
      method: 'GET'
    });

    if (!tokenResponse.success) {
      return {
        success: false,
        error: 'Authentication failed',
        status: 401
      };
    }

    const token = tokenResponse.data?.token;

    // Add mobileNumber, aadhaarNumber, aadhaarOTP, and refId as query parameters
    const url = new URL(API_ENDPOINTS.verifyCandidateAadhaar);
    url.searchParams.append('mobileNumber', mobile);
    url.searchParams.append('aadhaarNumber', aadhaarNumber);
    url.searchParams.append('aadhaarOTP', otp);
    url.searchParams.append('refId', refId);

    const response = await apiRequest(url.toString(), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        phoneNumber: mobile,
        otp: otp,
        aadhaarNumber: aadhaarNumber
      })
    });

    console.log('[ApiService] Aadhaar verification response:', response);
    return response;
  } catch (error) {
    console.error('[ApiService] Error verifying Aadhaar OTP:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      status: 500
    };
  }
};

/**
 * Verify PAN number
 * @param {string} panNumber - 10-character PAN number
 * @param {string} name - Name on PAN (optional)
 * @param {string} aadhaarNumber - Aadhaar number (optional)
 * @returns {Promise} - Verification result
 */
export const verifyPan = async (panNumber, name = null, aadhaarNumber = null) => {
  try {
    // Validate inputs early
    if (!panNumber || String(panNumber).trim().length === 0) {
      return {
        success: false,
        error: 'PAN number is required',
        status: 400
      };
    }

    const mobileNumber = getMobileNumber();
    if (!mobileNumber || mobileNumber === '0') {
      return {
        success: false,
        error: 'Mobile number is required',
        status: 400
      };
    }

    console.log('[ApiService] Verifying PAN:', panNumber);

    // Get programmatic token
    const tokenUrl = new URL(API_ENDPOINTS.userToken);
    tokenUrl.searchParams.append('mobileNumber', mobileNumber);
    
    const tokenResponse = await apiRequest(tokenUrl.toString(), {
      method: 'GET'
    });

    if (!tokenResponse.success) {
      return {
        success: false,
        error: 'Authentication failed',
        status: 401
      };
    }

    const token = tokenResponse.data?.token;

    // Add mobileNumber as query parameter
    const url = new URL(API_ENDPOINTS.verifyCandidatePAN);
    url.searchParams.append('mobileNumber', mobileNumber);
    // Also pass panNumber as query parameter as required by backend
    url.searchParams.append('panNumber', String(panNumber));

    const requestBody = {
      mobileNumber: String(mobileNumber),
      panNumber: String(panNumber)
    };

    if (name) requestBody.name = name;
    if (aadhaarNumber) requestBody.aadhaarNumber = aadhaarNumber;

    // Debug: Log exact payload being sent (safe fields only)
    try {
      console.log('[ApiService] PAN verification request body:', JSON.stringify(requestBody));
    } catch (_) {}

    const response = await apiRequest(url.toString(), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    console.log('[ApiService] PAN verification response:', response);
    return response;
  } catch (error) {
    console.error('[ApiService] Error verifying PAN:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      status: 500
    };
  }
};

/**
 * Update KYC status
 * @param {object} kycData - KYC data to update
 * @returns {Promise} - Update result
 */
export const updateKycStatus = async (kycData) => {
  try {
    const mobileNumber = getMobileNumber();
    if (!mobileNumber || mobileNumber === '0') {
      return {
        success: false,
        error: 'Mobile number is required',
        status: 400
      };
    }

    console.log('[ApiService] Updating KYC status:', kycData);

    // Get programmatic token
    const tokenUrl = new URL(API_ENDPOINTS.userToken);
    tokenUrl.searchParams.append('mobileNumber', mobileNumber);
    
    const tokenResponse = await apiRequest(tokenUrl.toString(), {
      method: 'GET'
    });

    if (!tokenResponse.success) {
      return {
        success: false,
        error: 'Authentication failed',
        status: 401
      };
    }

    const token = tokenResponse.data?.token;

    const response = await apiRequest(API_ENDPOINTS.updateKycStatus, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(kycData)
    });

    console.log('[ApiService] KYC status update response:', response);
    return response;
  } catch (error) {
    console.error('[ApiService] Error updating KYC status:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      status: 500
    };
  }
};

// ===== NOTIFICATION API FUNCTIONS =====

/**
 * Fetch notifications for applied jobs
 * @param {number} candidateId - Candidate ID
 * @returns {Promise} - API response with applied job notifications
 */
export const fetchAppliedJobNotifications = async (candidateId) => {
  console.log('[ApiService] Fetching applied job notifications for candidateId:', candidateId);
  
  try {
    const url = new URL(API_ENDPOINTS.fetchNotifications);
    url.searchParams.append('candidateId', candidateId.toString());
    url.searchParams.append('notificationType', 'AppliedJobUpdate');
    
    const response = await apiRequest(url.toString(), {
      method: 'GET'
    });
    
    if (response.success) {
      const notifications = Array.isArray(response.data) ? response.data : 
                          (response.data?.data ? response.data.data : []);
      
      // Filter by type to match Flutter implementation
      const filteredNotifications = notifications.filter(n => n.notificationType === 'AppliedJobUpdate');
      
      console.log('[ApiService] Applied job notifications fetched:', filteredNotifications.length);
      return {
        success: true,
        data: filteredNotifications
      };
    }
    
    return response;
  } catch (error) {
    console.error('[ApiService] Error fetching applied job notifications:', error);
    return {
      success: false,
      error: error.message || 'Network error'
    };
  }
};

/**
 * Fetch job opening notifications
 * @param {number} candidateId - Candidate ID
 * @returns {Promise} - API response with job opening notifications
 */
export const fetchJobOpeningNotifications = async (candidateId) => {
  console.log('[ApiService] Fetching job opening notifications for candidateId:', candidateId);
  
  try {
    const url = new URL(API_ENDPOINTS.fetchNotifications);
    url.searchParams.append('candidateId', candidateId.toString());
    url.searchParams.append('notificationType', 'JobOpening');
    
    const response = await apiRequest(url.toString(), {
      method: 'GET'
    });
    
    if (response.success) {
      const notifications = Array.isArray(response.data) ? response.data : 
                          (response.data?.data ? response.data.data : []);
      
      // Filter by type to match Flutter implementation
      const filteredNotifications = notifications.filter(n => n.notificationType === 'JobOpening');
      
      console.log('[ApiService] Job opening notifications fetched:', filteredNotifications.length);
      return {
        success: true,
        data: filteredNotifications
      };
    }
    
    return response;
  } catch (error) {
    console.error('[ApiService] Error fetching job opening notifications:', error);
    return {
      success: false,
      error: error.message || 'Network error'
    };
  }
};

// Get candidate details by mobile number
export const getCandidateDetailsByMobile = async (mobileNumber) => {
  try {
    console.log('[ApiService] Getting candidate details for mobile:', mobileNumber);
    
    // Use locally saved programmatic token first, fallback to authToken
    const token = localStorage.getItem('programmaticToken') || localStorage.getItem('authToken');
    if (!token) {
      console.error('[ApiService] No authentication token found');
      return { success: false, error: 'Authentication token not found' };
    }
    
    console.log('[ApiService] Using token for candidate details:', token?.substring(0, 20) + '...');

    const response = await fetch(`${API_ENDPOINTS.getCandidateDetailsByMobile}${mobileNumber}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });

    console.log('[ApiService] Candidate details response status:', response.status);
    
    if (response.ok) {
      const data = await response.json();
      console.log('[ApiService] Candidate details response:', data);
      return { success: true, data: data };
    } else {
      const errorText = await response.text();
      console.error('[ApiService] Failed to get candidate details:', errorText);
      return { success: false, error: `Failed to get candidate details: ${response.status}` };
    }
  } catch (error) {
    console.error('[ApiService] Error getting candidate details:', error);
    return { success: false, error: error.message || 'Network error' };
  }
};

// View Resume API - for viewing files within modal as blob URL
export const viewResumeFile = async (candidateId, filePath) => {
  try {
    console.log('[ApiService] Viewing resume file for candidate:', candidateId);
    console.log('[ApiService] FilePath:', filePath);

    // Get programmatic token (with caching)
    const token = await getProgrammaticToken();
    if (!token) {
      console.error('[ApiService] Failed to get programmatic token');
      return { success: false, error: 'Authentication failed. Please login again.' };
    }
    
    console.log('[ApiService] Got programmatic token successfully');

    // Use the exact filePath as backend 'filename' param
    const filename = filePath;
    
    // Path is constructed based on the pattern: candidates/documents/{candidateId}/
    const path = `candidates/documents/${candidateId}/`;
    
    console.log('[ApiService] View path:', path);
    console.log('[ApiService] View filename:', filename);

    // Create the URL with query parameters (without auth token in URL)
    const viewUrl = `${API_ENDPOINTS.downloadResume}?path=${encodeURIComponent(path)}&filename=${encodeURIComponent(filename)}`;
    
    console.log('[ApiService] View URL:', viewUrl);

    // Make authenticated request to get the file
    const response = await fetch(viewUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    console.log('[ApiService] View response status:', response.status);

    if (response.ok) {
      // Get the file blob and create object URL for viewing
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      console.log('✅ [ApiService] File blob created for viewing');
      return { 
        success: true, 
        data: {
          objectUrl,
          blob,
          contentType: blob.type,
          size: blob.size
        }
      };
    } else {
      const errorText = await response.text();
      console.error('❌ [ApiService] View failed:', response.status, errorText);
      throw new Error(`View failed: ${response.status} ${response.statusText}`);
    }
  } catch (error) {
    console.error('[ApiService] Error viewing resume:', error);
    return { success: false, error: error.message || 'Network error' };
  }
};

// Download Resume API - matching Flutter implementation
export const downloadResume = async (candidateId, filePath) => {
  try {
    console.log('🔥🔥🔥 [ApiService] DOWNLOAD RESUME API CALLED 🔥🔥🔥');
    console.log('[ApiService] Downloading resume for candidate:', candidateId);
    console.log('[ApiService] FilePath:', filePath);
    console.log('🔥 [ApiService] Expected endpoint should be: legacy-services/api/v1/user/downloadFileFromAWS');

    // Get programmatic token (with caching)
    const token = await getProgrammaticToken();
    if (!token) {
      console.error('[ApiService] Failed to get programmatic token');
      return { success: false, error: 'Authentication failed. Please login again.' };
    }
    
    console.log('[ApiService] Got programmatic token successfully');

    // Use the exact filePath as backend 'filename' param, but download with a safe basename
    const filename = filePath;
    
    // Path is constructed based on the pattern: candidates/documents/{candidateId}/
    const path = `candidates/documents/${candidateId}/`;
    
    console.log('[ApiService] Download path:', path);
    console.log('[ApiService] Download filename:', filename);

    // Create the URL with query parameters (without auth token in URL)
    const downloadUrl = `${API_ENDPOINTS.downloadResume}?path=${encodeURIComponent(path)}&filename=${encodeURIComponent(filename)}`;
    
    console.log('[ApiService] API_ENDPOINTS.downloadResume:', API_ENDPOINTS.downloadResume);
    console.log('[ApiService] Download URL (without token):', downloadUrl);
    console.log('[ApiService] About to make authenticated request...');
    
    // CRITICAL: Check if this URL contains the correct endpoint
    if (downloadUrl.includes('legacy-services/api/v1/user/downloadFileFromAWS')) {
      console.log('✅ [ApiService] CORRECT API ENDPOINT DETECTED!');
    } else {
      console.error('❌ [ApiService] WRONG API ENDPOINT! Expected: legacy-services/api/v1/user/downloadFileFromAWS');
      console.error('❌ [ApiService] Actual URL:', downloadUrl);
    }

    // Make authenticated request to get the file
    const response = await fetch(downloadUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    console.log('[ApiService] Download response status:', response.status);

    if (response.ok) {
      // Try to parse a filename from Content-Disposition; fallback to basename
      let disposition = response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
      let suggestedName = null;
      if (disposition && disposition.includes('filename')) {
        const match = disposition.match(/filename\*=UTF-8''([^;\n]+)|filename="?([^";\n]+)"?/i);
        suggestedName = decodeURIComponent((match && (match[1] || match[2] || '')).trim());
      }
      if (!suggestedName) {
        try { suggestedName = (filename || 'resume').split('/').pop(); } catch (_) { suggestedName = 'resume'; }
      }

      // Get the file blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Create temporary download link
      const a = document.createElement('a');
      a.href = url;
      a.download = suggestedName || 'resume';
      // Some browsers need the link in the DOM
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      console.log('✅ [ApiService] File download triggered with name:', suggestedName);
    } else {
      const errorText = await response.text();
      console.error('❌ [ApiService] Download failed:', response.status, errorText);
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }
    
    return { success: true, message: 'Resume download initiated' };
  } catch (error) {
    console.error('[ApiService] Error downloading resume:', error);
    return { success: false, error: error.message || 'Network error' };
  }
};

// Resume Upload API - using local token
export const updateCandidateResume = async (candidateId, file, filename) => {
  try {
    console.log('[ApiService] Updating resume for candidate:', candidateId);
    console.log('[ApiService] File:', filename, 'Size:', file.size);

    // Use locally saved programmatic token first, fallback to authToken
    let token = localStorage.getItem('programmaticToken') || localStorage.getItem('authToken');
    
    if (!token) {
      console.error('[ApiService] No authentication token found in localStorage');
      return { success: false, error: 'Authentication token not found. Please login again.' };
    }
    
    console.log('[ApiService] Using token from localStorage:', token?.substring(0, 20) + '...');

    // Create FormData for multipart upload
    const formData = new FormData();
    formData.append('candidateId', candidateId.toString());
    formData.append('resume', file, filename);

    console.log('[ApiService] Uploading to:', API_ENDPOINTS.updateCandidateResume);
    console.log('[ApiService] Using token:', token?.substring(0, 10) + '...');

    // Send the multipart request
    const response = await fetch(API_ENDPOINTS.updateCandidateResume, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        // Don't set Content-Type for FormData - browser will set it with boundary
      },
      body: formData
    });

    console.log('[ApiService] Resume upload response status:', response.status);
    
    const responseText = await response.text();
    console.log('[ApiService] Resume upload response body:', responseText);

    if (response.status === 200) {
      return { 
        success: true, 
        message: 'Resume uploaded successfully',
        data: responseText 
      };
    } else if (response.status === 400) {
      console.error('[ApiService] Bad Request: Resume file is required');
      return { 
        success: false, 
        error: 'Resume file is required or invalid format' 
      };
    } else {
      console.error('[ApiService] Resume upload failed with status:', response.status);
      return { 
        success: false, 
        error: `Upload failed with status ${response.status}` 
      };
    }

  } catch (error) {
    console.error('[ApiService] Error updating candidate resume:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to upload resume' 
    };
  }
};

/**
 * Execute direct transfer payout via legacy-services cashfree endpoint
 * POST with query parameters and 15s timeout
 * @param {Object} params
 * @param {string} params.mobileNumber
 * @param {string} params.vpa - UPI ID
 * @param {string} params.nameOnAadhar
 * @param {string|number} params.transferAmount
 * @returns {Promise<{success:boolean, message:string, status?:number}>}
 */
export const directTransfer = async ({ mobileNumber, vpa, nameOnAadhar, transferAmount }) => {
  try {
    const url = new URL(API_ENDPOINTS.directTransfer);
    url.searchParams.set('mobileNumber', String(mobileNumber || ''));
    url.searchParams.set('vpa', String(vpa || ''));
    url.searchParams.set('nameOnAadhar', String(nameOnAadhar || ''));
    url.searchParams.set('transferMode', 'upi');
    url.searchParams.set('transferAmount', String(transferAmount || ''));

    console.log('[ApiService] Direct transfer payout URL:', url.toString());

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const status = response.status;
    const text = await response.text();
    console.log('[ApiService] Direct transfer response:', { status, text });

    if (status === 200 && text) {
      return { success: true, message: text, status };
    }
    return { success: false, message: `Failed to transfer (HTTP ${status})`, status };
  } catch (e) {
    console.error('[ApiService] Direct transfer exception:', e);
    return { success: false, message: `Error during transfer: ${e?.message || e}` };
  }
};

const apiService = {
  // New unified job search
  searchJobs,
  // Legacy functions (use searchJobs instead)
  fetchJobsByCoordinates,
  fetchJobsWithFilters,
  // Other services
  getCurrentLocation,
  getDefaultLocation,
  getMobileNumber,
  saveCandidateDetails,
  applyForJob,
  getJobById,
  getApplications,
  getLastAppliedJobs,
  getReferrals,
  createReferral,
  getEarnings,
  getUserProfile,
  getCandidateByMobile,
  updateUserProfile,
  fetchCandidateSkills,
  saveCandidateSkills,
  markCandidateSkillsAsRemoved,
  fetchAllActiveSkills,
  geocodeAddress,
  getLocationResults,
  fetchPlaceIdDetails,
  saveCandidateLocation,
  getLocationDetailsFromCoordinates,
  getCityNameFromCoordinates,
  getLocationFromIP,
  // KYC verification
  fetchCandidateKYCDetails,
  requestAadhaarOtp,
  verifyAadhaarOtp,
  verifyAadhaarByUpload,
  verifyCandidateAadhaar,
  verifyPan,
  updateKycStatus,
  // Payments
  directTransfer,
  // Notifications
  fetchNotifications,
  markNotificationAsRead,
  setNotificationEngagementFlag,
  getTotalUnreadNotificationCount,
  fetchAppliedJobNotifications,
  fetchJobOpeningNotifications,
  // Job & Application Details
  getJobDetails,
  getApplicationStatus,
  // URL utilities
  getActualUrl,
  // Interview APIs
  fetchClientInterviews,
  fetchInterviewFeedback,
  submitInterviewFeedback,
  // User APIs
  fetchProgrammaticUser,
};

/**
 * Fetch filter options for jobs (locations, skills, designations)
 * @param {number} latitude - User's latitude
 * @param {number} longitude - User's longitude
 * @param {number} radius - Search radius in km (default: 150)
 * @returns {Promise<Object>} Filter options data
 */
export const fetchJobsFilterOptions = async (latitude, longitude, radius = 150) => {
  try {
    // Log the exact coordinates being sent
    console.log('[API] fetchJobsFilterOptions called with coordinates:', {
      latitude: latitude,
      longitude: longitude,
      radius: radius,
      latType: typeof latitude,
      lngType: typeof longitude
    });

    const url = new URL(API_ENDPOINTS.getJobsFilterOptions);
    url.searchParams.append('latitude', latitude.toString());
    url.searchParams.append('longitude', longitude.toString());
    url.searchParams.append('radius', radius.toString());

    console.log('[API] Fetching jobs filter options with URL:', url.toString());
    console.log('[API] Full request URL:', url.href);

    // Use shared helper to auto-inject token and retry on 401
    const resp = await apiRequest(url.toString(), { method: 'GET' });
    if (resp.success && resp.status === 200) {
      const data = resp.data;
      console.log('[API] Jobs Filter Options Data:', data);
      return data && typeof data === 'object' ? data : {};
    }
    console.error('[API] Failed to fetch filter options:', resp.status, resp.error);
    return {};
  } catch (error) {
    console.error('[API] Exception fetching filter options:', error);
    return {};
  }
};

export default apiService;
